import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ArrowLeft, Loader2, Pencil, Plus, Save, Search, Trash2, X } from 'lucide-react';
import { API_BASE, AUTH_AUDIENCE } from '../../config/env';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';
import { formatMoney } from '../../i18n/orgFormat';

type AppointmentTypeRow = {
  id?: string;
  name: string;
  category?: string | null;
  description?: string | null;
  durationMinutes: number;
  price: number;
  currency: string;
  available: boolean;
  bookingVisibility: 'PUBLIC' | 'STAFF_ONLY' | 'HIDDEN';
  locationOverrides: Array<Record<string, unknown>>;
  defaultStockRecipe: Array<Record<string, unknown>>;
  careInstructions: Record<string, unknown>;
  serviceDocumentation: Record<string, unknown>;
  clinicalSafetyRules: Record<string, unknown>;
  serviceGroup?: string | null;
  taxName?: string | null;
  taxRate?: number | null;
  displayOrder?: number | null;
  categoryColor?: string | null;
  serviceCode?: string | null;
  iscCode?: string | null;
  ccsdCode?: string | null;
  bufferBeforeMinutes?: number | null;
  bufferAfterMinutes?: number | null;
  extraBeforeMinutes?: number | null;
  extraAfterMinutes?: number | null;
  employeeDurations?: Record<string, number>;
  bookingLinkSlug?: string | null;
  bundledServiceIds?: string[];
  pointOfSaleEnabled?: boolean;
  serviceCharges?: ServiceChargeRow[];
  equipmentResourceIds?: string[];
  virtualDeliveryMode?: 'in_person' | 'virtual' | 'hybrid';
  videoProviderOverride?: string | null;
  groupVideoEnabled?: boolean;
  maxVideoParticipants?: number | null;
  autoCreateVideoSession?: boolean;
  videoConsultationInstructions?: string | null;
  videoTroubleshootingFaq?: string | null;
  isClass?: boolean;
  classCapacity?: number | null;
  classOnlineBookingEnabled?: boolean;
  classPortalVisible?: boolean;
  classNotificationsEnabled?: boolean;
  classWaitlistEnabled?: boolean;
  classParticipantInstructions?: string | null;
  color: string;
  isTelemed: boolean;
  portalSelfScheduleEnabled: boolean;
  existingPatientsOnly: boolean;
  allowNewPatients: boolean;
  portalDisplayName?: string | null;
  billingCodes?: string | null;
  cancellationRule?: string | null;
  allowPortalCancellation: boolean;
  cancellationNoticeHours: number;
  cancellationGracePeriodMinutes: number;
  lateCancellationFeeAmount?: number | null;
  lateCancellationFeeType: 'flat' | 'percent';
  chargeNoShowFee: boolean;
  requireCancellationReason: boolean;
  priority?: 'urgent' | 'normal' | 'low';
  complexity?: string | null;
  instructions?: string | null;
  serviceVariants?: Array<{
    id: string;
    code: string;
    name: string;
    category?: string | null;
    price: number;
    active: boolean;
  }>;
};

type BillingCodeRow = {
  id?: string;
  code: string;
  description: string;
  units: number;
  modifier: string;
  active: boolean;
  notes: string;
};

const appointmentTypesItOverrides: Record<string, string> = {
  'Appointment Types': 'Tipi di appuntamento',
  'Manage labels, duration, colors, billing links, and cancellation behavior.':
    'Gestisci etichette, durata, colori, collegamenti di fatturazione e comportamento di cancellazione.',
  'Add Type': 'Aggiungi tipo',
  'Loading appointment type...': 'Caricamento tipo appuntamento...',
  'Edit the appointment type, billing code rows, and cancellation logic from one page.':
    'Modifica tipo di appuntamento, righe codice di fatturazione e logica di cancellazione da un’unica pagina.',
};

function createAppointmentTypesTranslator(language: string, tr: (value: string) => string) {
  if (language !== 'it') return tr;
  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
    const local = appointmentTypesItOverrides[normalized];
    return local ? phrase.replace(normalized, local) : phrase;
  };
}

type BillingCatalogServiceRow = {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: string;
  active: boolean;
  scope: 'organization' | 'system';
};

type BillingCatalogResponse = {
  services: BillingCatalogServiceRow[];
  products?: BillingCatalogProductRow[];
};

type BillingCatalogProductRow = {
  id: string;
  code: string;
  name: string;
  description?: string;
  price: number;
  inventory: number;
  active: boolean;
  scope: 'organization' | 'system';
};

type InventoryItemOption = {
  id: string;
  name: string;
  baseUnit: string;
  category?: string | null;
  strength?: string | null;
  packageSize?: string | null;
};

type StockRecipeRow = {
  itemId?: string;
  billingProductId?: string;
  itemName: string;
  quantity: number;
  unit: string;
  brand?: string;
  notes?: string;
};

type ResourceOption = {
  id: string;
  name: string;
  type: string;
  locationId?: string | null;
  active: boolean;
};

type ServiceChargeRow = {
  id: string;
  name: string;
  code: string;
  amount: number;
  type: 'flat' | 'percent';
  active: boolean;
};

const emptyTypeForm: AppointmentTypeRow = {
  name: '',
  category: '',
  description: '',
  durationMinutes: 30,
  price: 0,
  currency: 'USD',
  available: true,
  bookingVisibility: 'PUBLIC',
  locationOverrides: [],
  defaultStockRecipe: [],
  careInstructions: {},
  serviceDocumentation: {},
  clinicalSafetyRules: {},
  serviceGroup: '',
  taxName: '',
  taxRate: 0,
  displayOrder: null,
  categoryColor: '#0ea5e9',
  serviceCode: '',
  iscCode: '',
  ccsdCode: '',
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  extraBeforeMinutes: 0,
  extraAfterMinutes: 0,
  employeeDurations: {},
  bookingLinkSlug: '',
  bundledServiceIds: [],
  pointOfSaleEnabled: true,
  serviceCharges: [],
  equipmentResourceIds: [],
  virtualDeliveryMode: 'in_person',
  videoProviderOverride: '',
  groupVideoEnabled: false,
  maxVideoParticipants: 2,
  autoCreateVideoSession: true,
  videoConsultationInstructions: '',
  videoTroubleshootingFaq: '',
  isClass: false,
  classCapacity: 8,
  classOnlineBookingEnabled: true,
  classPortalVisible: true,
  classNotificationsEnabled: true,
  classWaitlistEnabled: false,
  classParticipantInstructions: '',
  color: '#0ea5e9',
  isTelemed: false,
  portalSelfScheduleEnabled: true,
  existingPatientsOnly: false,
  allowNewPatients: true,
  portalDisplayName: '',
  billingCodes: '',
  cancellationRule: '',
  allowPortalCancellation: true,
  cancellationNoticeHours: 24,
  cancellationGracePeriodMinutes: 0,
  lateCancellationFeeAmount: null,
  lateCancellationFeeType: 'flat',
  chargeNoShowFee: false,
  requireCancellationReason: false,
  priority: 'normal',
  complexity: '',
  instructions: '',
  serviceVariants: [],
};

const emptyBillingCodeForm: BillingCodeRow = {
  code: '',
  description: '',
  units: 1,
  modifier: '',
  active: true,
  notes: '',
};

const showPrefill = import.meta.env.DEV;

type AppointmentTypePayload = AppointmentTypeRow & {
  id: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
};

type AppointmentTypeDetailPayload = AppointmentTypePayload & {
  billingCodeItems: Array<
    BillingCodeRow & {
      id: string;
      appointmentTypeId: string;
      createdAt: string;
      updatedAt: string;
    }
  >;
};

type TreatmentCatalogConfig = {
  enableServiceVariants: boolean;
};

function normalizeType(row?: Partial<AppointmentTypeRow> & Record<string, any>): AppointmentTypeRow {
  const serviceDocumentation = row?.serviceDocumentation && typeof row.serviceDocumentation === 'object' ? row.serviceDocumentation as Record<string, any> : {};
  const serviceMeta = serviceDocumentation.serviceMeta && typeof serviceDocumentation.serviceMeta === 'object' ? serviceDocumentation.serviceMeta as Record<string, any> : {};
  return {
    name: row?.name ?? '',
    category: row?.category ?? '',
    description: row?.description ?? '',
    durationMinutes: Number(row?.durationMinutes ?? 30),
    price: Number(row?.price ?? 0),
    currency: row?.currency ?? 'USD',
    available: Boolean(row?.available ?? true),
    bookingVisibility: row?.bookingVisibility === 'STAFF_ONLY' || row?.bookingVisibility === 'HIDDEN' ? row.bookingVisibility : 'PUBLIC',
    locationOverrides: Array.isArray(row?.locationOverrides) ? row.locationOverrides : [],
    defaultStockRecipe: Array.isArray(row?.defaultStockRecipe) ? row.defaultStockRecipe : [],
    careInstructions: row?.careInstructions && typeof row.careInstructions === 'object' ? row.careInstructions : {},
    serviceDocumentation,
    clinicalSafetyRules: row?.clinicalSafetyRules && typeof row.clinicalSafetyRules === 'object' ? row.clinicalSafetyRules : {},
    serviceGroup: serviceMeta.serviceGroup ?? row?.serviceGroup ?? '',
    taxName: serviceMeta.taxName ?? row?.taxName ?? '',
    taxRate: serviceMeta.taxRate === null || serviceMeta.taxRate === undefined ? 0 : Number(serviceMeta.taxRate),
    displayOrder: serviceMeta.displayOrder === null || serviceMeta.displayOrder === undefined ? null : Number(serviceMeta.displayOrder),
    categoryColor: serviceMeta.categoryColor ?? row?.categoryColor ?? '#0ea5e9',
    serviceCode: serviceMeta.serviceCode ?? row?.serviceCode ?? '',
    iscCode: serviceMeta.iscCode ?? row?.iscCode ?? '',
    ccsdCode: serviceMeta.ccsdCode ?? row?.ccsdCode ?? '',
    bufferBeforeMinutes: serviceMeta.bufferBeforeMinutes === null || serviceMeta.bufferBeforeMinutes === undefined ? 0 : Number(serviceMeta.bufferBeforeMinutes),
    bufferAfterMinutes: serviceMeta.bufferAfterMinutes === null || serviceMeta.bufferAfterMinutes === undefined ? 0 : Number(serviceMeta.bufferAfterMinutes),
    extraBeforeMinutes: serviceMeta.extraBeforeMinutes === null || serviceMeta.extraBeforeMinutes === undefined ? 0 : Number(serviceMeta.extraBeforeMinutes),
    extraAfterMinutes: serviceMeta.extraAfterMinutes === null || serviceMeta.extraAfterMinutes === undefined ? 0 : Number(serviceMeta.extraAfterMinutes),
    employeeDurations: serviceMeta.employeeDurations && typeof serviceMeta.employeeDurations === 'object' && !Array.isArray(serviceMeta.employeeDurations) ? serviceMeta.employeeDurations as Record<string, number> : {},
    bookingLinkSlug: serviceMeta.bookingLinkSlug ?? row?.bookingLinkSlug ?? '',
    bundledServiceIds: Array.isArray(serviceMeta.bundledServiceIds) ? serviceMeta.bundledServiceIds.map(String) : [],
    pointOfSaleEnabled: Boolean(serviceMeta.pointOfSaleEnabled ?? row?.pointOfSaleEnabled ?? true),
    serviceCharges: Array.isArray(serviceMeta.serviceCharges) ? serviceMeta.serviceCharges.map(normalizeServiceCharge) : [],
    equipmentResourceIds: Array.isArray(serviceMeta.equipmentResourceIds) ? serviceMeta.equipmentResourceIds.map(String) : [],
    virtualDeliveryMode: serviceMeta.virtualDeliveryMode === 'virtual' || serviceMeta.virtualDeliveryMode === 'hybrid' ? serviceMeta.virtualDeliveryMode : (row?.isTelemed ? 'virtual' : 'in_person'),
    videoProviderOverride: serviceMeta.videoProviderOverride ?? '',
    groupVideoEnabled: Boolean(serviceMeta.groupVideoEnabled),
    maxVideoParticipants: serviceMeta.maxVideoParticipants === null || serviceMeta.maxVideoParticipants === undefined ? 2 : Number(serviceMeta.maxVideoParticipants),
    autoCreateVideoSession: Boolean(serviceMeta.autoCreateVideoSession ?? true),
    videoConsultationInstructions: serviceMeta.videoConsultationInstructions ?? '',
    videoTroubleshootingFaq: serviceMeta.videoTroubleshootingFaq ?? '',
    isClass: Boolean(serviceMeta.isClass),
    classCapacity: serviceMeta.classCapacity === null || serviceMeta.classCapacity === undefined ? 8 : Number(serviceMeta.classCapacity),
    classOnlineBookingEnabled: Boolean(serviceMeta.classOnlineBookingEnabled ?? true),
    classPortalVisible: Boolean(serviceMeta.classPortalVisible ?? true),
    classNotificationsEnabled: Boolean(serviceMeta.classNotificationsEnabled ?? true),
    classWaitlistEnabled: Boolean(serviceMeta.classWaitlistEnabled),
    classParticipantInstructions: serviceMeta.classParticipantInstructions ?? '',
    color: row?.color ?? '#0ea5e9',
    isTelemed: Boolean(row?.isTelemed),
    portalSelfScheduleEnabled: Boolean(row?.portalSelfScheduleEnabled ?? true),
    existingPatientsOnly: Boolean(row?.existingPatientsOnly),
    allowNewPatients: Boolean(row?.allowNewPatients ?? true),
    portalDisplayName: row?.portalDisplayName ?? '',
    billingCodes: row?.billingCodes ?? '',
    cancellationRule: row?.cancellationRule ?? '',
    allowPortalCancellation: Boolean(row?.allowPortalCancellation ?? true),
    cancellationNoticeHours: Number(row?.cancellationNoticeHours ?? 24),
    cancellationGracePeriodMinutes: Number(row?.cancellationGracePeriodMinutes ?? 0),
    lateCancellationFeeAmount:
      row?.lateCancellationFeeAmount === null || row?.lateCancellationFeeAmount === undefined
        ? null
        : Number(row?.lateCancellationFeeAmount),
    lateCancellationFeeType: row?.lateCancellationFeeType === 'percent' ? 'percent' : 'flat',
    chargeNoShowFee: Boolean(row?.chargeNoShowFee),
    requireCancellationReason: Boolean(row?.requireCancellationReason),
    priority: row?.priority === 'urgent' || row?.priority === 'low' ? row.priority : 'normal',
    complexity: row?.complexity ?? '',
    instructions: row?.instructions ?? '',
    serviceVariants: Array.isArray(row?.serviceVariants) ? row.serviceVariants : [],
  };
}

function normalizeBillingCode(row?: Partial<BillingCodeRow> & Record<string, any>): BillingCodeRow {
  return {
    ...emptyBillingCodeForm,
    ...row,
    code: row?.code ?? '',
    description: row?.description ?? '',
    units: Number(row?.units ?? 1),
    modifier: row?.modifier ?? '',
    active: Boolean(row?.active ?? true),
    notes: row?.notes ?? '',
  };
}

function normalizeStockRecipe(value: unknown): StockRecipeRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((row) => {
      const source = row && typeof row === 'object' ? row as Record<string, unknown> : {};
      return {
        itemId: typeof source.itemId === 'string' ? source.itemId : undefined,
        billingProductId: typeof source.billingProductId === 'string' ? source.billingProductId : undefined,
        itemName: String(source.itemName || ''),
        quantity: Number(source.quantity || 1),
        unit: String(source.unit || 'unit'),
        brand: typeof source.brand === 'string' ? source.brand : '',
        notes: typeof source.notes === 'string' ? source.notes : '',
      };
    })
    .filter((row) => row.itemName.trim() && row.quantity > 0);
}

function serviceMeta(row: Pick<AppointmentTypeRow, 'serviceDocumentation'> & Partial<AppointmentTypeRow>) {
  const docs = row.serviceDocumentation && typeof row.serviceDocumentation === 'object' ? row.serviceDocumentation as Record<string, any> : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  return {
    serviceGroup: String(row.serviceGroup ?? meta.serviceGroup ?? ''),
    taxName: String(row.taxName ?? meta.taxName ?? ''),
    taxRate: Number(row.taxRate ?? meta.taxRate ?? 0),
    displayOrder: row.displayOrder === null || row.displayOrder === undefined
      ? (meta.displayOrder === null || meta.displayOrder === undefined ? null : Number(meta.displayOrder))
      : Number(row.displayOrder),
    categoryColor: String(row.categoryColor ?? meta.categoryColor ?? '#0ea5e9'),
    serviceCode: String(row.serviceCode ?? meta.serviceCode ?? ''),
    iscCode: String(row.iscCode ?? meta.iscCode ?? ''),
    ccsdCode: String(row.ccsdCode ?? meta.ccsdCode ?? ''),
    bufferBeforeMinutes: Number(row.bufferBeforeMinutes ?? meta.bufferBeforeMinutes ?? 0),
    bufferAfterMinutes: Number(row.bufferAfterMinutes ?? meta.bufferAfterMinutes ?? 0),
    extraBeforeMinutes: Number(row.extraBeforeMinutes ?? meta.extraBeforeMinutes ?? 0),
    extraAfterMinutes: Number(row.extraAfterMinutes ?? meta.extraAfterMinutes ?? 0),
    employeeDurations: row.employeeDurations ?? (meta.employeeDurations && typeof meta.employeeDurations === 'object' && !Array.isArray(meta.employeeDurations) ? meta.employeeDurations as Record<string, number> : {}),
    bookingLinkSlug: String(row.bookingLinkSlug ?? meta.bookingLinkSlug ?? ''),
    bundledServiceIds: Array.isArray(row.bundledServiceIds) ? row.bundledServiceIds : (Array.isArray(meta.bundledServiceIds) ? meta.bundledServiceIds.map(String) : []),
    pointOfSaleEnabled: Boolean(row.pointOfSaleEnabled ?? meta.pointOfSaleEnabled ?? true),
    serviceCharges: Array.isArray(row.serviceCharges) ? row.serviceCharges : (Array.isArray(meta.serviceCharges) ? meta.serviceCharges.map(normalizeServiceCharge) : []),
    equipmentResourceIds: Array.isArray(row.equipmentResourceIds) ? row.equipmentResourceIds : (Array.isArray(meta.equipmentResourceIds) ? meta.equipmentResourceIds.map(String) : []),
    virtualDeliveryMode: row.virtualDeliveryMode === 'virtual' || row.virtualDeliveryMode === 'hybrid' ? row.virtualDeliveryMode : (meta.virtualDeliveryMode === 'virtual' || meta.virtualDeliveryMode === 'hybrid' ? meta.virtualDeliveryMode : 'in_person'),
    videoProviderOverride: String(row.videoProviderOverride ?? meta.videoProviderOverride ?? ''),
    groupVideoEnabled: Boolean(row.groupVideoEnabled ?? meta.groupVideoEnabled ?? false),
    maxVideoParticipants: Number(row.maxVideoParticipants ?? meta.maxVideoParticipants ?? 2),
    autoCreateVideoSession: Boolean(row.autoCreateVideoSession ?? meta.autoCreateVideoSession ?? true),
    videoConsultationInstructions: String(row.videoConsultationInstructions ?? meta.videoConsultationInstructions ?? ''),
    videoTroubleshootingFaq: String(row.videoTroubleshootingFaq ?? meta.videoTroubleshootingFaq ?? ''),
    isClass: Boolean(row.isClass ?? meta.isClass ?? false),
    classCapacity: Number(row.classCapacity ?? meta.classCapacity ?? 8),
    classOnlineBookingEnabled: Boolean(row.classOnlineBookingEnabled ?? meta.classOnlineBookingEnabled ?? true),
    classPortalVisible: Boolean(row.classPortalVisible ?? meta.classPortalVisible ?? true),
    classNotificationsEnabled: Boolean(row.classNotificationsEnabled ?? meta.classNotificationsEnabled ?? true),
    classWaitlistEnabled: Boolean(row.classWaitlistEnabled ?? meta.classWaitlistEnabled ?? false),
    classParticipantInstructions: String(row.classParticipantInstructions ?? meta.classParticipantInstructions ?? ''),
  };
}

function normalizeServiceCharge(row: Partial<ServiceChargeRow> & Record<string, any>): ServiceChargeRow {
  return {
    id: String(row.id || `charge-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`),
    name: String(row.name || ''),
    code: String(row.code || 'SVC-CHARGE').toUpperCase(),
    amount: Number(row.amount || 0),
    type: row.type === 'percent' ? 'percent' : 'flat',
    active: Boolean(row.active ?? true),
  };
}

function isRoomResourceType(type: string) {
  return /room|suite|bay|bed|ward|or|theatre|theater|icu/i.test(type);
}

function serviceSortValue(row: AppointmentTypePayload) {
  const meta = serviceMeta(row);
  return meta.displayOrder ?? 999999;
}

function uniqueInventoryItems(rows: InventoryItemOption[]) {
  const map = new Map<string, InventoryItemOption>();
  rows.forEach((row) => {
    if (!map.has(row.id)) map.set(row.id, row);
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function BillingCodeModal({
  open,
  form,
  onClose,
  onChange,
  onSave,
  saving,
  isEdit,
  catalogRows,
  catalogLoading,
}: {
  open: boolean;
  form: BillingCodeRow;
  onClose: () => void;
  onChange: (next: BillingCodeRow) => void;
  onSave: (event: FormEvent) => void;
  saving: boolean;
  isEdit: boolean;
  catalogRows: BillingCatalogServiceRow[];
  catalogLoading: boolean;
}) {
  const [catalogSearch, setCatalogSearch] = useState('');

  useEffect(() => {
    if (!open) {
      setCatalogSearch('');
    }
  }, [open]);

  const filteredCatalogRows = useMemo(() => {
    const query = catalogSearch.trim().toLowerCase();
    if (!query) return catalogRows.slice(0, 8);
    return catalogRows
      .filter((row) => {
        return (
          row.code.toLowerCase().includes(query) ||
          row.name.toLowerCase().includes(query) ||
          (row.category || '').toLowerCase().includes(query) ||
          (row.description || '').toLowerCase().includes(query)
        );
      })
      .slice(0, 8);
  }, [catalogRows, catalogSearch]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 px-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-ink">{isEdit ? 'Edit billing code' : 'Add billing code'}</h3>
            <p className="text-sm text-slate-500">Add the billing items that belong to this appointment type.</p>
          </div>
          <button onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-ink" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSave} className="space-y-4">
          <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div>
              <h4 className="text-sm font-medium text-ink">Search billing catalog</h4>
              <p className="text-xs text-slate-500">Search services and procedures from Billing Catalog and use one to prefill this row.</p>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={catalogSearch}
                onChange={(event) => setCatalogSearch(event.target.value)}
                placeholder="Search code, name, category, description..."
              />
            </div>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white">
              {catalogLoading ? (
                <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-slate-500">
                  <Loader2 size={14} className="animate-spin" />
                  Loading billing catalog...
                </div>
              ) : filteredCatalogRows.length ? (
                filteredCatalogRows.map((row) => {
                  const selected = form.code.trim().toLowerCase() === row.code.toLowerCase();
                  return (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() =>
                        onChange({
                          ...form,
                          code: row.code,
                          description: row.description || row.name,
                        })
                      }
                      className={`flex w-full items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 ${
                        selected ? 'bg-sky-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-ink">{row.code}</div>
                        <div className="truncate text-sm text-slate-700">{row.name}</div>
                        <div className="truncate text-xs text-slate-500">{row.description || 'No description'}</div>
                      </div>
                      <div className="shrink-0 text-right text-xs text-slate-500">
                        <div>{row.category || 'General'}</div>
                        <div>{row.scope === 'organization' ? 'Organization' : 'System'}</div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-6 text-sm text-slate-500">No catalog matches found.</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Code</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.code}
                onChange={(event) => onChange({ ...form, code: event.target.value })}
                required
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Units</span>
              <input
                type="number"
                min={1}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.units}
                onChange={(event) => onChange({ ...form, units: Number(event.target.value) || 1 })}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Modifier</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.modifier}
                onChange={(event) => onChange({ ...form, modifier: event.target.value })}
                placeholder="25, 59, etc."
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Status</span>
              <select
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.active ? 'active' : 'inactive'}
                onChange={(event) => onChange({ ...form, active: event.target.value === 'active' })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>

          <label className="space-y-1 text-sm block">
            <span className="font-medium text-slate-500">Description</span>
            <textarea
              className="h-24 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
              value={form.description}
              onChange={(event) => onChange({ ...form, description: event.target.value })}
              placeholder="Short explanation of the billing code"
            />
          </label>

          <label className="space-y-1 text-sm block">
            <span className="font-medium text-slate-500">Notes</span>
            <textarea
              className="h-24 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
              value={form.notes}
              onChange={(event) => onChange({ ...form, notes: event.target.value })}
              placeholder="Internal notes or payer-specific guidance"
            />
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-60">
              <Save size={16} /> {saving ? 'Saving...' : isEdit ? 'Save billing code' : 'Add billing code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AppointmentTypeListPage({
  rows,
  loading,
  error,
  success,
  serviceVariantsEnabled,
  configSaving,
  onAdd,
  onOpen,
  onDelete,
  onUpdate,
  onToggleServiceVariants,
  onBack,
}: {
  rows: AppointmentTypePayload[];
  loading: boolean;
  error: string | null;
  success: string | null;
  serviceVariantsEnabled: boolean;
  configSaving: boolean;
  onAdd: () => void;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, patch: Partial<AppointmentTypeRow>) => void;
  onToggleServiceVariants: (next: boolean) => void;
  onBack: () => void;
}) {
  const { language, tr } = useI18n();
  const ta = createAppointmentTypesTranslator(language, tr);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [setupFilter, setSetupFilter] = useState<'all' | 'portal' | 'staff' | 'telemed' | 'existing' | 'pos' | 'bundled' | 'classes'>('all');
  const [sortMode, setSortMode] = useState<'manual' | 'alpha'>('manual');
  const [categoryDraft, setCategoryDraft] = useState('');
  const [groupDraft, setGroupDraft] = useState('');
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [customGroups, setCustomGroups] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkGroup, setBulkGroup] = useState('');
  const [bulkIscCode, setBulkIscCode] = useState('');
  const [bulkCcsdCode, setBulkCcsdCode] = useState('');
  const [bulkBufferBefore, setBulkBufferBefore] = useState('');
  const [bulkBufferAfter, setBulkBufferAfter] = useState('');
  const [bulkExtraBefore, setBulkExtraBefore] = useState('');
  const [bulkExtraAfter, setBulkExtraAfter] = useState('');
  const [bulkOnlineBooking, setBulkOnlineBooking] = useState<'keep' | 'enabled' | 'disabled'>('keep');
  const categories = useMemo(
    () => Array.from(new Set([...rows.map((row) => row.category || 'General'), ...customCategories])).sort((a, b) => a.localeCompare(b)),
    [customCategories, rows],
  );
  const groups = useMemo(
    () => Array.from(new Set([...rows.map((row) => serviceMeta(row).serviceGroup || 'Ungrouped'), ...customGroups])).sort((a, b) => a.localeCompare(b)),
    [customGroups, rows],
  );
  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const result = rows.filter((row) => {
      const meta = serviceMeta(row);
      const matchesQuery = !q || [row.name, row.category, row.description, meta.serviceGroup, meta.taxName, meta.serviceCode, meta.iscCode, meta.ccsdCode, row.billingCodes].some((value) => String(value || '').toLowerCase().includes(q));
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? row.available : !row.available);
      const matchesCategory = categoryFilter === 'all' || (row.category || 'General') === categoryFilter;
      const matchesGroup = groupFilter === 'all' || (meta.serviceGroup || 'Ungrouped') === groupFilter;
      const matchesSetup =
        setupFilter === 'all' ||
        (setupFilter === 'portal' && row.portalSelfScheduleEnabled) ||
        (setupFilter === 'staff' && row.bookingVisibility === 'STAFF_ONLY') ||
        (setupFilter === 'telemed' && row.isTelemed) ||
        (setupFilter === 'existing' && row.existingPatientsOnly) ||
        (setupFilter === 'pos' && meta.pointOfSaleEnabled) ||
        (setupFilter === 'bundled' && meta.bundledServiceIds.length > 0) ||
        (setupFilter === 'classes' && meta.isClass);
      return matchesQuery && matchesStatus && matchesCategory && matchesGroup && matchesSetup;
    });
    return result.sort((left, right) => {
      if (sortMode === 'alpha') return left.name.localeCompare(right.name);
      const manual = serviceSortValue(left) - serviceSortValue(right);
      return manual !== 0 ? manual : left.name.localeCompare(right.name);
    });
  }, [categoryFilter, groupFilter, query, rows, setupFilter, sortMode, statusFilter]);

  function renameCategory(category: string) {
    const next = window.prompt('Rename category', category)?.trim();
    if (!next || next === category) return;
    setCustomCategories((current) => current.map((item) => (item === category ? next : item)));
    rows
      .filter((row) => (row.category || 'General') === category && row.id)
      .forEach((row) => onUpdate(row.id, { category: next }));
  }

  function clearCategory(category: string) {
    if (!window.confirm(`Remove category "${category}" from matching services?`)) return;
    setCustomCategories((current) => current.filter((item) => item !== category));
    rows
      .filter((row) => (row.category || 'General') === category && row.id)
      .forEach((row) => onUpdate(row.id, { category: '' }));
  }

  function renameGroup(group: string) {
    const next = window.prompt('Rename group', group === 'Ungrouped' ? '' : group)?.trim();
    if (!next || next === group) return;
    setCustomGroups((current) => current.map((item) => (item === group ? next : item)));
    rows
      .filter((row) => (serviceMeta(row).serviceGroup || 'Ungrouped') === group && row.id)
      .forEach((row) => onUpdate(row.id, { serviceGroup: next }));
  }

  function clearGroup(group: string) {
    if (!window.confirm(`Remove group "${group}" from matching services?`)) return;
    setCustomGroups((current) => current.filter((item) => item !== group));
    rows
      .filter((row) => (serviceMeta(row).serviceGroup || 'Ungrouped') === group && row.id)
      .forEach((row) => onUpdate(row.id, { serviceGroup: '' }));
  }

  function createCategory() {
    const next = categoryDraft.trim();
    if (!next) return;
    setCustomCategories((current) => current.includes(next) ? current : [...current, next]);
    setCategoryFilter(next);
    setCategoryDraft('');
  }

  function createGroup() {
    const next = groupDraft.trim();
    if (!next) return;
    setCustomGroups((current) => current.includes(next) ? current : [...current, next]);
    setGroupFilter(next);
    setGroupDraft('');
  }

  function applyAlphabeticalOrder() {
    rows
      .slice()
      .sort((left, right) => left.name.localeCompare(right.name))
      .forEach((row, index) => {
        if (row.id) onUpdate(row.id, { displayOrder: index + 1 });
      });
    setSortMode('manual');
  }

  function setCategoryColor(category: string, color: string) {
    rows
      .filter((row) => (row.category || 'General') === category && row.id)
      .forEach((row) => onUpdate(row.id, { categoryColor: color }));
  }

  function toggleSelected(id: string, selected: boolean) {
    setSelectedIds((current) => selected ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id));
  }

  function applyBulkEdit() {
    const ids = selectedIds.length ? selectedIds : filteredRows.map((row) => row.id).filter(Boolean);
    const patch: Partial<AppointmentTypeRow> = {};
    if (bulkCategory.trim()) patch.category = bulkCategory.trim();
    if (bulkGroup.trim()) patch.serviceGroup = bulkGroup.trim();
    if (bulkIscCode.trim()) patch.iscCode = bulkIscCode.trim();
    if (bulkCcsdCode.trim()) patch.ccsdCode = bulkCcsdCode.trim();
    if (bulkBufferBefore.trim()) patch.bufferBeforeMinutes = Math.max(0, Number(bulkBufferBefore) || 0);
    if (bulkBufferAfter.trim()) patch.bufferAfterMinutes = Math.max(0, Number(bulkBufferAfter) || 0);
    if (bulkExtraBefore.trim()) patch.extraBeforeMinutes = Math.max(0, Number(bulkExtraBefore) || 0);
    if (bulkExtraAfter.trim()) patch.extraAfterMinutes = Math.max(0, Number(bulkExtraAfter) || 0);
    if (bulkOnlineBooking !== 'keep') {
      patch.portalSelfScheduleEnabled = bulkOnlineBooking === 'enabled';
      patch.bookingVisibility = bulkOnlineBooking === 'enabled' ? 'PUBLIC' : 'STAFF_ONLY';
    }
    if (!Object.keys(patch).length) return;
    ids.forEach((id) => onUpdate(id, patch));
    setBulkCategory('');
    setBulkGroup('');
    setBulkIscCode('');
    setBulkCcsdCode('');
    setBulkBufferBefore('');
    setBulkBufferAfter('');
    setBulkExtraBefore('');
    setBulkExtraAfter('');
    setBulkOnlineBooking('keep');
    setSelectedIds([]);
  }

  function reorderService(row: AppointmentTypePayload, direction: -1 | 1) {
    const ordered = [...rows].sort((left, right) => serviceSortValue(left) - serviceSortValue(right) || left.name.localeCompare(right.name));
    const index = ordered.findIndex((item) => item.id === row.id);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= ordered.length || !row.id) return;
    const target = ordered[nextIndex];
    const currentOrder = serviceSortValue(row);
    const targetOrder = serviceSortValue(target);
    onUpdate(row.id, { displayOrder: targetOrder });
    if (target.id) onUpdate(target.id, { displayOrder: currentOrder });
  }
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="rounded-full p-2 hover:bg-slate-100" aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{ta('Services')}</h1>
            <p className="text-sm text-slate-500">{ta('Create, edit, price, tax, categorize, group, color, and reorder services.')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {success ? <span className="text-sm text-emerald-600">{success}</span> : null}
          <button onClick={onAdd} className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-white shadow-soft">
            <Plus size={16} /> {ta('Add Service')}
          </button>
        </div>
      </div>

      {error ? <div className="text-sm text-rose-600">{error}</div> : null}

      <div className="card flex items-center justify-between gap-4 p-4">
        <div>
          <p className="text-sm font-semibold text-ink">Service Management</p>
          <p className="text-xs text-slate-500">Keep this off for the simple appointment-type workflow. Turn it on only when your clinic needs linked service options.</p>
        </div>
        <button
          type="button"
          onClick={() => onToggleServiceVariants(!serviceVariantsEnabled)}
          disabled={configSaving}
          className={`rounded-lg px-4 py-2 text-sm font-medium ${serviceVariantsEnabled ? 'bg-ink text-white' : 'border border-slate-300 bg-white text-slate-700'} disabled:opacity-60`}
        >
          {configSaving ? 'Saving...' : serviceVariantsEnabled ? 'Enabled' : 'Disabled'}
        </button>
      </div>

      <div className="card space-y-4 p-4">
        <div className="grid gap-3 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr]">
          <label className="space-y-1 text-sm">
            <span className="font-medium text-slate-500">Search services</span>
            <input className="w-full rounded-lg border border-slate-200 px-3 py-2" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, category, group, billing..." />
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-slate-500">Status</span>
            <select className="w-full rounded-lg border border-slate-200 px-3 py-2" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'inactive')}>
              <option value="all">All services</option>
              <option value="active">Active services</option>
              <option value="inactive">Inactive services</option>
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-slate-500">Category</span>
            <select className="w-full rounded-lg border border-slate-200 px-3 py-2" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
              <option value="all">All categories</option>
              {categories.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-slate-500">Group</span>
            <select className="w-full rounded-lg border border-slate-200 px-3 py-2" value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)}>
              <option value="all">All groups</option>
              {groups.map((group) => <option key={group} value={group}>{group}</option>)}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-slate-500">Setup</span>
            <select className="w-full rounded-lg border border-slate-200 px-3 py-2" value={setupFilter} onChange={(event) => setSetupFilter(event.target.value as 'all' | 'portal' | 'staff' | 'telemed' | 'existing' | 'pos' | 'bundled' | 'classes')}>
              <option value="all">Any setup</option>
              <option value="portal">Portal visible</option>
              <option value="staff">Staff only</option>
              <option value="telemed">Telemed</option>
              <option value="existing">Existing clients only</option>
              <option value="pos">POS enabled</option>
              <option value="bundled">Bundled services</option>
              <option value="classes">Classes</option>
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium text-slate-500">Ordering</span>
            <select className="w-full rounded-lg border border-slate-200 px-3 py-2" value={sortMode} onChange={(event) => setSortMode(event.target.value as 'manual' | 'alpha')}>
              <option value="manual">Manual order</option>
              <option value="alpha">Alphabetical</option>
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={applyAlphabeticalOrder} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Apply alphabetical order
          </button>
          <span className="text-xs text-slate-500">Manual arrows persist each service's display order.</span>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-ink">Service Categories</p>
                <p className="text-xs text-slate-500">Rename, delete by clearing, and reorder categories through service assignment.</p>
              </div>
              <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-slate-500">{categories.length}</span>
            </div>
              <div className="mt-3 flex gap-2">
              <input className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={categoryDraft} onChange={(event) => setCategoryDraft(event.target.value)} placeholder="New category name" />
              <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" onClick={createCategory}>Create</button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {categories.map((category) => (
                <span key={category} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700">
                  <input
                    type="color"
                    value={rows.find((row) => (row.category || 'General') === category)?.categoryColor || serviceMeta(rows.find((row) => (row.category || 'General') === category) || emptyTypeForm).categoryColor}
                    onChange={(event) => setCategoryColor(category, event.target.value)}
                    className="h-5 w-6 rounded border border-slate-200 bg-white p-0"
                    aria-label={`Set ${category} color`}
                  />
                  <button type="button" onClick={() => setCategoryFilter(category)} className="px-1 hover:text-ink">{category}</button>
                  <button type="button" onClick={() => renameCategory(category)} className="rounded px-1 text-slate-400 hover:bg-slate-100 hover:text-ink" aria-label={`Rename ${category}`}>Edit</button>
                  <button type="button" onClick={() => clearCategory(category)} className="rounded px-1 text-slate-400 hover:bg-rose-50 hover:text-rose-700" aria-label={`Delete ${category}`}>Delete</button>
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-ink">Service Groups</p>
                <p className="text-xs text-slate-500">Groups organize related services without changing booking categories.</p>
              </div>
              <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-slate-500">{groups.length}</span>
            </div>
            <div className="mt-3 flex gap-2">
              <input className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={groupDraft} onChange={(event) => setGroupDraft(event.target.value)} placeholder="New group name" />
              <button type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" onClick={createGroup}>Create</button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {groups.map((group) => (
                <span key={group} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700">
                  <button type="button" onClick={() => setGroupFilter(group)} className="px-1 hover:text-ink">{group}</button>
                  <button type="button" onClick={() => renameGroup(group)} className="rounded px-1 text-slate-400 hover:bg-slate-100 hover:text-ink" aria-label={`Rename ${group}`}>Edit</button>
                  <button type="button" onClick={() => clearGroup(group)} className="rounded px-1 text-slate-400 hover:bg-rose-50 hover:text-rose-700" aria-label={`Delete ${group}`}>Delete</button>
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-ink">Bulk Service Editor</p>
              <p className="text-xs text-slate-500">Apply category, group, online booking, codes, buffer time, or extra service time to selected services or the current filtered list.</p>
            </div>
            <span className="text-xs text-slate-500">{selectedIds.length || filteredRows.length} targeted</span>
          </div>
          <div className="mt-3 grid gap-2 md:grid-cols-5">
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" value={bulkCategory} onChange={(event) => setBulkCategory(event.target.value)} placeholder="Category" />
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" value={bulkGroup} onChange={(event) => setBulkGroup(event.target.value)} placeholder="Group" />
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase" value={bulkIscCode} onChange={(event) => setBulkIscCode(event.target.value.toUpperCase())} placeholder="ISC code" />
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase" value={bulkCcsdCode} onChange={(event) => setBulkCcsdCode(event.target.value.toUpperCase())} placeholder="CCSD code" />
            <select className="rounded-lg border border-slate-200 px-3 py-2 text-sm" value={bulkOnlineBooking} onChange={(event) => setBulkOnlineBooking(event.target.value as 'keep' | 'enabled' | 'disabled')}>
              <option value="keep">Keep online booking</option>
              <option value="enabled">Enable online booking</option>
              <option value="disabled">Disable online booking</option>
            </select>
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" type="number" min={0} value={bulkBufferBefore} onChange={(event) => setBulkBufferBefore(event.target.value)} placeholder="Buffer before min" />
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" type="number" min={0} value={bulkBufferAfter} onChange={(event) => setBulkBufferAfter(event.target.value)} placeholder="Buffer after min" />
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" type="number" min={0} value={bulkExtraBefore} onChange={(event) => setBulkExtraBefore(event.target.value)} placeholder="Extra before min" />
            <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" type="number" min={0} value={bulkExtraAfter} onChange={(event) => setBulkExtraAfter(event.target.value)} placeholder="Extra after min" />
          </div>
          <button type="button" onClick={applyBulkEdit} className="mt-3 rounded-lg bg-ink px-3 py-2 text-sm font-semibold text-white">
            Apply bulk edit
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Catalog</p>
            <h3 className="font-semibold text-ink">Services</h3>
          </div>
          {loading ? <span className="text-sm text-slate-500">Loading...</span> : null}
        </div>

        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="w-10 p-4">
                <input
                  type="checkbox"
                  checked={Boolean(filteredRows.length) && filteredRows.every((row) => selectedIds.includes(row.id))}
                  onChange={(event) => setSelectedIds(event.target.checked ? filteredRows.map((row) => row.id) : [])}
                  aria-label="Select all filtered services"
                />
              </th>
              <th className="p-4">Name</th>
              <th className="p-4">Code</th>
              <th className="p-4">Category</th>
              <th className="p-4">Group</th>
              <th className="p-4">Price</th>
              <th className="p-4">Tax</th>
              <th className="p-4">Time</th>
              {serviceVariantsEnabled ? <th className="p-4">Linked Services</th> : null}
              <th className="p-4">Billing Codes</th>
              <th className="p-4">Cancellation</th>
              <th className="p-4">Booking</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredRows.map((row) => {
              const meta = serviceMeta(row);
              return (
              <tr
                key={row.id}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => row.id && onOpen(row.id)}
              >
                <td className="p-4" onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={(event) => toggleSelected(row.id, event.target.checked)}
                    aria-label={`Select ${row.name}`}
                  />
                </td>
                <td className="p-4 font-medium text-ink">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full border" style={{ backgroundColor: row.color }} />
                    {row.name}
                  </div>
                </td>
                <td className="p-4 text-slate-600">{meta.serviceCode || '—'}</td>
                <td className="p-4 text-slate-600">{row.category || 'General'}</td>
                <td className="p-4 text-slate-600">{meta.serviceGroup || 'Ungrouped'}</td>
                <td className="p-4 text-slate-600">
                  {formatMoney(Number(row.price || 0), { currency: row.currency || undefined })}
                </td>
                <td className="p-4 text-slate-600">{meta.taxRate ? `${meta.taxName || 'Tax'} ${meta.taxRate}%` : 'No tax'}</td>
                <td className="p-4 text-slate-600">
                  <div>{row.durationMinutes} min</div>
                  <div className="text-xs text-slate-400">B {meta.bufferBeforeMinutes}/{meta.bufferAfterMinutes} · X {meta.extraBeforeMinutes}/{meta.extraAfterMinutes}</div>
                </td>
                {serviceVariantsEnabled ? <td className="p-4 text-slate-600">{row.serviceVariants?.length || 0}</td> : null}
                <td className="p-4 text-slate-600">
                  <div>{row.billingCodes || '—'}</div>
                  <div className="text-xs text-slate-400">{[meta.iscCode && `ISC ${meta.iscCode}`, meta.ccsdCode && `CCSD ${meta.ccsdCode}`].filter(Boolean).join(' · ') || 'No ISC/CCSD'}</div>
                </td>
                <td className="p-4 text-slate-600">{`${row.cancellationNoticeHours}h notice`}</td>
                <td className="p-4 text-slate-600">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (row.id) onUpdate(row.id, { available: !row.available });
                    }}
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${row.available ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                  >
                    {row.available ? 'Enabled' : 'Disabled'}
                  </button>
                  <div className="mt-1 text-xs text-slate-500">{row.portalSelfScheduleEnabled ? 'Online' : 'Hidden'} · {row.existingPatientsOnly ? 'Existing' : 'All clients'}</div>
                  <div className="mt-1 text-xs text-slate-400">{meta.pointOfSaleEnabled ? 'POS' : 'No POS'}{meta.bundledServiceIds.length ? ` · ${meta.bundledServiceIds.length} bundle` : ''}</div>
                </td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-1">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      reorderService(row, -1);
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                    title="Move up"
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      reorderService(row, 1);
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                    title="Move down"
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (row.id) onDelete(row.id);
                    }}
                    title="Delete"
                    aria-label="Delete"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 size={14} />
                  </button>
                  </div>
                </td>
              </tr>
            );})}
            {!filteredRows.length && !loading ? (
              <tr>
                <td colSpan={serviceVariantsEnabled ? 13 : 12} className="p-6 text-center text-slate-500">
                  No services found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AppointmentTypeDetailPage({
  mode,
  id,
  serviceVariantsEnabled,
  onBack,
  onSaved,
}: {
  mode: 'new' | 'edit';
  id?: string;
  serviceVariantsEnabled: boolean;
  onBack: () => void;
  onSaved: () => Promise<void>;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const { fetchJson } = useApiClient();
  const navigate = useNavigate();
  const { language, tr } = useI18n();
  const ta = createAppointmentTypesTranslator(language, tr);
  const [form, setForm] = useState<AppointmentTypeRow>(emptyTypeForm);
  const [locationOverridesJson, setLocationOverridesJson] = useState('[]');
  const [defaultStockRecipeJson, setDefaultStockRecipeJson] = useState('[]');
  const [stockRecipeRows, setStockRecipeRows] = useState<StockRecipeRow[]>([]);
  const [careInstructionsJson, setCareInstructionsJson] = useState('{}');
  const [serviceDocumentationJson, setServiceDocumentationJson] = useState('{}');
  const [clinicalSafetyRulesJson, setClinicalSafetyRulesJson] = useState('{}');
  const [billingCodes, setBillingCodes] = useState<BillingCodeRow[]>([]);
  const [serviceOptions, setServiceOptions] = useState<AppointmentTypePayload[]>([]);
  const [catalogRows, setCatalogRows] = useState<BillingCatalogServiceRow[]>([]);
  const [billingProducts, setBillingProducts] = useState<BillingCatalogProductRow[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItemOption[]>([]);
  const [resourceOptions, setResourceOptions] = useState<ResourceOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailSuccess, setDetailSuccess] = useState<string | null>(null);
  const [billingCodeModalOpen, setBillingCodeModalOpen] = useState(false);
  const [billingCodeForm, setBillingCodeForm] = useState<BillingCodeRow>(emptyBillingCodeForm);
  const [billingCodeSaving, setBillingCodeSaving] = useState(false);
  const [billingCodeError, setBillingCodeError] = useState<string | null>(null);
  const [descriptionGenerating, setDescriptionGenerating] = useState(false);

  const getToken = useCallback(async () => {
    return getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
  }, [getAccessTokenSilently]);

  const loadDetail = useCallback(async () => {
    if (mode === 'new' || !id) {
      setForm(emptyTypeForm);
      setLocationOverridesJson('[]');
      setDefaultStockRecipeJson('[]');
      setStockRecipeRows([]);
      setBillingCodes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setDetailError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/appointment-types/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Load failed ${res.status}`);
      const data = (await res.json()) as AppointmentTypeDetailPayload;
      setForm(normalizeType(data));
      setLocationOverridesJson(JSON.stringify(data.locationOverrides || [], null, 2));
      const recipeRows = normalizeStockRecipe(data.defaultStockRecipe || []);
      setStockRecipeRows(recipeRows);
      setDefaultStockRecipeJson(JSON.stringify(recipeRows, null, 2));
      setCareInstructionsJson(JSON.stringify(data.careInstructions || {}, null, 2));
      setServiceDocumentationJson(JSON.stringify(data.serviceDocumentation || {}, null, 2));
      setClinicalSafetyRulesJson(JSON.stringify(data.clinicalSafetyRules || {}, null, 2));
      setBillingCodes((data.billingCodeItems || []).map(normalizeBillingCode));
    } catch (err: any) {
      setDetailError(err.message || 'Failed to load appointment type');
    } finally {
      setLoading(false);
    }
  }, [getToken, id, mode]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const loadServiceOptions = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/appointment-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Load failed ${res.status}`);
      const data = (await res.json()) as AppointmentTypePayload[];
      setServiceOptions((data || []).filter((row) => row.id !== id));
    } catch {
      setServiceOptions([]);
    }
  }, [getToken, id]);

  useEffect(() => {
    void loadServiceOptions();
  }, [loadServiceOptions]);

  const loadBillingCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const data = await fetchJson<BillingCatalogResponse>('/billing/catalog?includeInactive=false');
      setCatalogRows(Array.isArray(data.services) ? data.services.filter((row) => row.active) : []);
      setBillingProducts(Array.isArray(data.products) ? data.products.filter((row) => row.active) : []);
    } catch {
      setCatalogRows([]);
      setBillingProducts([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    void loadBillingCatalog();
  }, [loadBillingCatalog]);

  const loadInventoryItems = useCallback(async () => {
    try {
      const data = await fetchJson<{ data: InventoryItemOption[] }>('/inventory/summary');
      setInventoryItems(uniqueInventoryItems(Array.isArray(data.data) ? data.data : []));
    } catch {
      setInventoryItems([]);
    }
  }, [fetchJson]);

  useEffect(() => {
    void loadInventoryItems();
  }, [loadInventoryItems]);

  const loadResourceOptions = useCallback(async () => {
    try {
      const rows = await fetchJson<ResourceOption[]>('/resources?includeInactive=true');
      setResourceOptions(Array.isArray(rows) ? rows : []);
    } catch {
      setResourceOptions([]);
    }
  }, [fetchJson]);

  useEffect(() => {
    void loadResourceOptions();
  }, [loadResourceOptions]);

  const prefill = () => {
    setForm({
      ...emptyTypeForm,
      name: 'New Patient Intake',
      category: 'Wellness',
      description: 'Initial wellness consult and treatment planning visit.',
      durationMinutes: 45,
      price: 125,
      currency: 'USD',
      available: true,
      bookingVisibility: 'PUBLIC',
      color: '#10b981',
      isTelemed: true,
      portalSelfScheduleEnabled: true,
      existingPatientsOnly: false,
      allowNewPatients: true,
      portalDisplayName: 'New Patient Intake',
      cancellationNoticeHours: 24,
      cancellationGracePeriodMinutes: 30,
      lateCancellationFeeAmount: 25,
      lateCancellationFeeType: 'flat',
      chargeNoShowFee: true,
      requireCancellationReason: true,
      complexity: 'Moderate',
      instructions: 'Collect insurance card, verify demographics, obtain consent, add vitals.',
      careInstructions: {
        preCare: 'Hydrate well before the visit.',
        postCare: 'Avoid strenuous activity for the rest of the day.',
        aftercare: 'Call the clinic if symptoms worsen.',
        followUp: 'Check in after 48 hours if needed.',
        locationOverrides: [],
      },
      serviceDocumentation: {
        intakeFormTemplateIds: [],
        consentFormTemplateIds: [],
        contraindicationQuestions: ['Any allergy, pregnancy, or medication contraindications?'],
        signedAcknowledgementRequired: false,
        requireCompletionBeforeTreatment: false,
      },
      clinicalSafetyRules: {
        enabled: false,
        requireAllergyReview: true,
        requireMedicationReview: true,
        requirePregnancyReview: false,
        requireContraindicationReview: true,
        requireSuitabilityConfirmation: true,
        doctorEscalationRequired: false,
        adverseEventRecordingEnabled: true,
      },
    });
    setLocationOverridesJson('[]');
    const demoRecipe = [{ itemName: 'Intake supplies', quantity: 1, unit: 'kit' }];
    setStockRecipeRows(demoRecipe);
    setDefaultStockRecipeJson(JSON.stringify(demoRecipe, null, 2));
    setBillingCodes([
      {
        code: '99204',
        description: 'New patient office visit',
        units: 1,
        modifier: '',
        active: true,
        notes: '',
      },
      {
        code: '99385',
        description: 'Initial preventive exam',
        units: 1,
        modifier: '',
        active: true,
        notes: '',
      },
    ]);
  };

  function openBillingCodeModal(row?: BillingCodeRow) {
    setBillingCodeForm(row ? { ...row } : emptyBillingCodeForm);
    setBillingCodeError(null);
    setBillingCodeModalOpen(true);
  }

  function updateEmployeeDuration(employeeId: string, minutes: number) {
    if (!employeeId.trim()) return;
    setForm((prev) => ({
      ...prev,
      employeeDurations: {
        ...(prev.employeeDurations || {}),
        [employeeId.trim()]: Math.max(0, minutes),
      },
    }));
  }

  function removeEmployeeDuration(employeeId: string) {
    setForm((prev) => {
      const next = { ...(prev.employeeDurations || {}) };
      delete next[employeeId];
      return { ...prev, employeeDurations: next };
    });
  }

  function addEmployeeDuration() {
    const employeeId = window.prompt('Employee ID');
    if (!employeeId?.trim()) return;
    const minutes = Number(window.prompt('Custom duration in minutes', String(form.durationMinutes || 30)) || form.durationMinutes || 30);
    updateEmployeeDuration(employeeId, minutes);
  }

  async function generateServiceDescription() {
    setDescriptionGenerating(true);
    setDetailError(null);
    try {
      const result = await fetchJson<{ contentText: string }>('/agent/echo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task: 'service-description',
          serviceName: form.name || 'New service',
          category: form.category || form.serviceGroup || undefined,
          sourceText: [form.description, form.instructions, form.complexity].filter(Boolean).join('\n\n'),
        }),
      });
      setForm((prev) => ({ ...prev, description: result.contentText }));
      setDetailSuccess('Echo AI service description inserted.');
      setTimeout(() => setDetailSuccess(null), 2500);
    } catch (err: any) {
      setDetailError(err?.message || 'Service description generator failed.');
    } finally {
      setDescriptionGenerating(false);
    }
  }

  function addServiceCharge() {
    setForm((prev) => ({
      ...prev,
      serviceCharges: [
        ...(prev.serviceCharges || []),
        {
          id: `charge-${Date.now()}`,
          name: 'New service charge',
          code: 'SVC-CHARGE',
          amount: 0,
          type: 'flat',
          active: true,
        },
      ],
    }));
  }

  function updateServiceCharge(chargeId: string, patch: Partial<ServiceChargeRow>) {
    setForm((prev) => ({
      ...prev,
      serviceCharges: (prev.serviceCharges || []).map((charge) => charge.id === chargeId ? normalizeServiceCharge({ ...charge, ...patch }) : charge),
    }));
  }

  function deleteServiceCharge(chargeId: string) {
    if (!window.confirm('Delete this service charge?')) return;
    setForm((prev) => ({
      ...prev,
      serviceCharges: (prev.serviceCharges || []).filter((charge) => charge.id !== chargeId),
    }));
  }

  async function saveAppointmentType(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      setDetailError('Name is required.');
      return;
    }
    setSaving(true);
    setDetailError(null);
    try {
      const locationOverrides = JSON.parse(locationOverridesJson || '[]');
      const defaultStockRecipe = JSON.parse(defaultStockRecipeJson || '[]');
      const careInstructions = JSON.parse(careInstructionsJson || '{}');
      const serviceDocumentation = JSON.parse(serviceDocumentationJson || '{}');
      const clinicalSafetyRules = JSON.parse(clinicalSafetyRulesJson || '{}');
      if (!Array.isArray(locationOverrides) || !Array.isArray(defaultStockRecipe) || !careInstructions || Array.isArray(careInstructions) || typeof careInstructions !== 'object' || !serviceDocumentation || Array.isArray(serviceDocumentation) || typeof serviceDocumentation !== 'object' || !clinicalSafetyRules || Array.isArray(clinicalSafetyRules) || typeof clinicalSafetyRules !== 'object') {
        throw new Error('Location overrides and default stock recipe must be JSON arrays; care instructions, service documentation, and clinical safety rules must be JSON objects.');
      }
      const existingMeta = serviceDocumentation.serviceMeta && typeof serviceDocumentation.serviceMeta === 'object' && !Array.isArray(serviceDocumentation.serviceMeta)
        ? serviceDocumentation.serviceMeta as Record<string, unknown>
        : {};
      const serviceDocumentationWithMeta = {
        ...serviceDocumentation,
        serviceMeta: {
          ...existingMeta,
          serviceGroup: form.serviceGroup || '',
          taxName: form.taxName || '',
          taxRate: Number(form.taxRate || 0),
          displayOrder: form.displayOrder === null || form.displayOrder === undefined ? null : Number(form.displayOrder),
          categoryColor: form.categoryColor || '#0ea5e9',
          serviceCode: form.serviceCode || '',
          iscCode: form.iscCode || '',
          ccsdCode: form.ccsdCode || '',
          bufferBeforeMinutes: Number(form.bufferBeforeMinutes || 0),
          bufferAfterMinutes: Number(form.bufferAfterMinutes || 0),
          extraBeforeMinutes: Number(form.extraBeforeMinutes || 0),
          extraAfterMinutes: Number(form.extraAfterMinutes || 0),
          employeeDurations: form.employeeDurations || {},
          bookingLinkSlug: form.bookingLinkSlug || '',
          bundledServiceIds: form.bundledServiceIds || [],
          pointOfSaleEnabled: Boolean(form.pointOfSaleEnabled ?? true),
          serviceCharges: (form.serviceCharges || []).map(normalizeServiceCharge),
          equipmentResourceIds: form.equipmentResourceIds || [],
          virtualDeliveryMode: form.virtualDeliveryMode || (form.isTelemed ? 'virtual' : 'in_person'),
          videoProviderOverride: form.videoProviderOverride || '',
          groupVideoEnabled: Boolean(form.groupVideoEnabled),
          maxVideoParticipants: Number(form.maxVideoParticipants || 2),
          autoCreateVideoSession: Boolean(form.autoCreateVideoSession ?? true),
          videoConsultationInstructions: form.videoConsultationInstructions || '',
          videoTroubleshootingFaq: form.videoTroubleshootingFaq || '',
          isClass: Boolean(form.isClass),
          classCapacity: Number(form.classCapacity || 1),
          classOnlineBookingEnabled: Boolean(form.classOnlineBookingEnabled ?? true),
          classPortalVisible: Boolean(form.classPortalVisible ?? true),
          classNotificationsEnabled: Boolean(form.classNotificationsEnabled ?? true),
          classWaitlistEnabled: Boolean(form.classWaitlistEnabled),
          classParticipantInstructions: form.classParticipantInstructions || '',
        },
      };
      const token = await getToken();
      const payload = {
        ...form,
        category: form.category || null,
        description: form.description || null,
        currency: form.currency || 'USD',
        locationOverrides,
        defaultStockRecipe,
        careInstructions,
        serviceDocumentation: serviceDocumentationWithMeta,
        clinicalSafetyRules,
        billingCodes: billingCodes.length ? billingCodes.map((row) => row.code.trim()).filter(Boolean).join(', ') : form.billingCodes || null,
        portalDisplayName: form.portalDisplayName || null,
        complexity: form.complexity || null,
        instructions: form.instructions || null,
        cancellationRule: form.cancellationRule || null,
        lateCancellationFeeAmount: form.lateCancellationFeeAmount == null || form.lateCancellationFeeAmount === undefined ? null : Number(form.lateCancellationFeeAmount),
      };
      const isEdit = mode === 'edit' && id;
      const res = await fetch(`${API_BASE}/appointment-types${isEdit ? `/${id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`${isEdit ? 'Update' : 'Create'} failed ${res.status}`);
      const saved = (await res.json()) as AppointmentTypePayload;
      setDetailSuccess('Saved');
      setTimeout(() => setDetailSuccess(null), 2500);
      if (mode === 'new') {
        if (saved.id && billingCodes.length) {
          for (const row of billingCodes) {
            const codeRes = await fetch(`${API_BASE}/appointment-types/${saved.id}/billing-codes`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                code: row.code,
                description: row.description || null,
                units: row.units,
                modifier: row.modifier || null,
                active: row.active,
                notes: row.notes || null,
              }),
            });
            if (!codeRes.ok) throw new Error(`Create billing code failed ${codeRes.status}`);
          }
        }
        if (saved.id) {
          navigate(`/settings/appointmenttypes/${saved.id}`, { replace: true });
        }
        await onSaved();
        return;
      }
      await loadDetail();
      if (saved.id) {
        await onSaved();
      }
    } catch (err: any) {
      setDetailError(err.message || 'Failed to save appointment type');
    } finally {
      setSaving(false);
    }
  }

  function syncStockRecipe(next: StockRecipeRow[]) {
    setStockRecipeRows(next);
    setDefaultStockRecipeJson(JSON.stringify(next, null, 2));
  }

  function addStockRecipeRow() {
    syncStockRecipe([...stockRecipeRows, { itemName: '', quantity: 1, unit: 'unit' }]);
  }

  function updateStockRecipeRow(index: number, patch: Partial<StockRecipeRow>) {
    const next = stockRecipeRows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row);
    syncStockRecipe(next);
  }

  function removeStockRecipeRow(index: number) {
    syncStockRecipe(stockRecipeRows.filter((_, rowIndex) => rowIndex !== index));
  }

  function selectInventoryItem(index: number, itemId: string) {
    const item = inventoryItems.find((row) => row.id === itemId);
    updateStockRecipeRow(index, {
      itemId: itemId || undefined,
      itemName: item?.name || stockRecipeRows[index]?.itemName || '',
      unit: item?.baseUnit || stockRecipeRows[index]?.unit || 'unit',
    });
  }

  function selectBillingProduct(index: number, billingProductId: string) {
    const product = billingProducts.find((row) => row.id === billingProductId);
    updateStockRecipeRow(index, {
      billingProductId: billingProductId || undefined,
      itemName: product?.name || stockRecipeRows[index]?.itemName || '',
    });
  }

  async function deleteAppointmentType() {
    if (!id) return;
    if (!window.confirm('Delete this appointment type?')) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/appointment-types/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed ${res.status}`);
      onBack();
      await onSaved();
    } catch (err: any) {
      setDetailError(err.message || 'Failed to delete appointment type');
    }
  }

  async function saveBillingCode(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    if (!billingCodeForm.code.trim()) {
      setBillingCodeError('Code is required.');
      return;
    }
    setBillingCodeSaving(true);
    setBillingCodeError(null);
    try {
      const token = await getToken();
      const isEdit = Boolean(billingCodeForm.id);
      const res = await fetch(`${API_BASE}/appointment-types/${id}/billing-codes${isEdit ? `/${billingCodeForm.id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          code: billingCodeForm.code,
          description: billingCodeForm.description || null,
          units: billingCodeForm.units,
          modifier: billingCodeForm.modifier || null,
          active: billingCodeForm.active,
          notes: billingCodeForm.notes || null,
        }),
      });
      if (!res.ok) throw new Error(`Save failed ${res.status}`);
      setBillingCodeModalOpen(false);
      await loadDetail();
      await onSaved();
    } catch (err: any) {
      setBillingCodeError(err.message || 'Failed to save billing code');
    } finally {
      setBillingCodeSaving(false);
    }
  }

  async function deleteBillingCode(codeId?: string) {
    if (!id || !codeId) return;
    if (!window.confirm('Delete this billing code row?')) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/appointment-types/${id}/billing-codes/${codeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed ${res.status}`);
      await loadDetail();
      await onSaved();
    } catch (err: any) {
      setDetailError(err.message || 'Failed to delete billing code');
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="rounded-full p-2 hover:bg-slate-100" aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <div className="text-sm text-slate-500">{ta('Loading appointment type...')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="rounded-full p-2 hover:bg-slate-100" aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{mode === 'new' ? tr('New Appointment Type') : form.name || ta('Appointment Types')}</h1>
            <p className="text-sm text-slate-500">{ta('Edit the appointment type, billing code rows, and cancellation logic from one page.')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {showPrefill && mode === 'new' ? (
            <button
              type="button"
              onClick={prefill}
              className="rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-600 hover:border-ink hover:text-ink"
            >
              Prefill demo data
            </button>
          ) : null}
          {detailSuccess ? <span className="text-sm text-emerald-600">{detailSuccess}</span> : null}
          <button
            onClick={saveAppointmentType}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save size={16} /> {saving ? tr('Saving...') : tr('Save Changes')}
          </button>
          {mode === 'edit' ? (
            <button
              type="button"
              onClick={deleteAppointmentType}
              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-4 py-2 text-rose-700 hover:bg-rose-50"
            >
              <Trash2 size={16} /> Delete
            </button>
          ) : null}
        </div>
      </div>

      {detailError ? <div className="text-sm text-rose-600">{detailError}</div> : null}

      {serviceVariantsEnabled ? (
        <div className="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          Appointment types are session wrappers for scheduling, duration, portal rules, and operational defaults.
          Concrete treatments and service options should be managed in Billing Catalog and linked back to this appointment type.
        </div>
      ) : null}

      {mode === 'edit' && serviceVariantsEnabled ? (
        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-ink">Linked Service Options</h3>
              <p className="text-sm text-slate-500">These are the concrete treatments that can be booked under this appointment type.</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/settings/billing-catalog')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <Pencil size={14} /> Manage in Billing Catalog
            </button>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-100">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="p-3">Code</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Price</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(form.serviceVariants || []).map((service) => (
                  <tr key={service.id}>
                    <td className="p-3 font-medium text-ink">{service.code}</td>
                    <td className="p-3 text-slate-700">{service.name}</td>
                    <td className="p-3 text-slate-600">{service.category || 'General'}</td>
                    <td className="p-3 text-slate-600">${Number(service.price || 0).toFixed(2)}</td>
                    <td className="p-3 text-slate-600">{service.active ? 'Active' : 'Inactive'}</td>
                  </tr>
                ))}
                {!(form.serviceVariants || []).length ? (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-slate-500">No linked service options yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <form onSubmit={saveAppointmentType} className="space-y-6">
        <div className="card p-5 space-y-4">
          <h3 className="font-semibold text-ink">Core Details</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Name</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                required
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Duration (minutes)</span>
              <input
                type="number"
                min={5}
                max={480}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.durationMinutes}
                onChange={(event) => setForm((prev) => ({ ...prev, durationMinutes: Number(event.target.value) || 30 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Category</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.category || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
                placeholder="Vitamin drips, boosters, aesthetics"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Service group</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.serviceGroup || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, serviceGroup: event.target.value }))}
                placeholder="Injectables, Skin, Wellness"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Category color</span>
              <input
                type="color"
                className="h-11 w-full rounded-lg border border-slate-200 bg-white px-2 py-1 outline-none"
                value={form.categoryColor || '#0ea5e9'}
                onChange={(event) => setForm((prev) => ({ ...prev, categoryColor: event.target.value }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Service code</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 uppercase outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.serviceCode || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, serviceCode: event.target.value.toUpperCase() }))}
                placeholder="SVC-001"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Price</span>
              <input
                type="number"
                min={0}
                step="0.01"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.price}
                onChange={(event) => setForm((prev) => ({ ...prev, price: Number(event.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Currency</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 uppercase outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.currency}
                onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))}
                maxLength={8}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Tax name</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.taxName || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, taxName: event.target.value }))}
                placeholder="Sales tax, VAT, GST"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Tax rate (%)</span>
              <input
                type="number"
                min={0}
                step="0.001"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.taxRate ?? 0}
                onChange={(event) => setForm((prev) => ({ ...prev, taxRate: Number(event.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Manual order</span>
              <input
                type="number"
                min={0}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.displayOrder ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm((prev) => ({ ...prev, displayOrder: value === '' ? null : Number(value) }));
                }}
                placeholder="Lower numbers appear first"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">ISC code</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 uppercase outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.iscCode || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, iscCode: event.target.value.toUpperCase() }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">CCSD code</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 uppercase outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.ccsdCode || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, ccsdCode: event.target.value.toUpperCase() }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Color</span>
              <input
                type="color"
                className="h-11 w-full rounded-lg border border-slate-200 bg-white px-2 py-1 outline-none"
                value={form.color}
                onChange={(event) => setForm((prev) => ({ ...prev, color: event.target.value }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Portal display name</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.portalDisplayName || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, portalDisplayName: event.target.value }))}
                placeholder="Visible to patients"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Priority</span>
              <select
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.priority}
                onChange={(event) => setForm((prev) => ({ ...prev, priority: event.target.value as 'urgent' | 'normal' | 'low' }))}
              >
                <option value="urgent">Urgent</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </label>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={form.isTelemed}
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  isTelemed: event.target.checked,
                  virtualDeliveryMode: event.target.checked && prev.virtualDeliveryMode === 'in_person' ? 'virtual' : prev.virtualDeliveryMode,
                  autoCreateVideoSession: event.target.checked ? prev.autoCreateVideoSession ?? true : prev.autoCreateVideoSession,
                }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Virtual service</span>
            </div>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={form.portalSelfScheduleEnabled}
                onChange={(event) => setForm((prev) => ({ ...prev, portalSelfScheduleEnabled: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Show in portal</span>
            </div>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Booking visibility</span>
              <select
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.bookingVisibility}
                onChange={(event) => setForm((prev) => ({ ...prev, bookingVisibility: event.target.value as 'PUBLIC' | 'STAFF_ONLY' | 'HIDDEN', portalSelfScheduleEnabled: event.target.value === 'PUBLIC' }))}
              >
                <option value="PUBLIC">Public booking</option>
                <option value="STAFF_ONLY">Staff only</option>
                <option value="HIDDEN">Hidden</option>
              </select>
            </label>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={form.available}
                onChange={(event) => setForm((prev) => ({ ...prev, available: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Available</span>
            </div>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={form.existingPatientsOnly}
                onChange={(event) => setForm((prev) => ({ ...prev, existingPatientsOnly: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Existing patients only</span>
            </div>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={Boolean(form.pointOfSaleEnabled)}
                onChange={(event) => setForm((prev) => ({ ...prev, pointOfSaleEnabled: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Sell through POS</span>
            </div>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={form.allowNewPatients}
                onChange={(event) => setForm((prev) => ({ ...prev, allowNewPatients: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Allow new patients</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Buffer before (minutes)</span>
              <input
                type="number"
                min={0}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.bufferBeforeMinutes ?? 0}
                onChange={(event) => setForm((prev) => ({ ...prev, bufferBeforeMinutes: Number(event.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Buffer after (minutes)</span>
              <input
                type="number"
                min={0}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.bufferAfterMinutes ?? 0}
                onChange={(event) => setForm((prev) => ({ ...prev, bufferAfterMinutes: Number(event.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Extra time before (minutes)</span>
              <input
                type="number"
                min={0}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.extraBeforeMinutes ?? 0}
                onChange={(event) => setForm((prev) => ({ ...prev, extraBeforeMinutes: Number(event.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Extra time after (minutes)</span>
              <input
                type="number"
                min={0}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.extraAfterMinutes ?? 0}
                onChange={(event) => setForm((prev) => ({ ...prev, extraAfterMinutes: Number(event.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Custom booking link slug</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.bookingLinkSlug || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, bookingLinkSlug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                placeholder="hydration-consult"
              />
              {form.bookingLinkSlug ? (
                <span className="block break-all text-xs text-slate-500">/book/services/{form.bookingLinkSlug}</span>
              ) : null}
            </label>
            <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 md:col-span-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="text-sm font-medium text-slate-600">Classes</span>
                  <p className="text-xs text-slate-500">Use this service as a class template for group calendar bookings and online class booking.</p>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${form.isClass ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {form.isClass ? 'Class enabled' : 'One-to-one'}
                </span>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="flex items-start gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                    checked={Boolean(form.isClass)}
                    onChange={(event) => setForm((prev) => ({
                      ...prev,
                      isClass: event.target.checked,
                      classCapacity: event.target.checked ? Math.max(1, Number(prev.classCapacity || 8)) : prev.classCapacity,
                    }))}
                  />
                  <span>
                    <span className="block font-medium text-slate-700">Class management</span>
                    <span className="block text-xs text-slate-500">Enable participant capacity and class booking controls.</span>
                  </span>
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-500">Capacity</span>
                  <input
                    type="number"
                    min={1}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    value={form.classCapacity ?? 8}
                    onChange={(event) => setForm((prev) => ({ ...prev, classCapacity: Math.max(1, Number(event.target.value) || 1) }))}
                  />
                </label>
                <label className="flex items-start gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                    checked={Boolean(form.classNotificationsEnabled ?? true)}
                    onChange={(event) => setForm((prev) => ({ ...prev, classNotificationsEnabled: event.target.checked }))}
                  />
                  <span>
                    <span className="block font-medium text-slate-700">Class notifications</span>
                    <span className="block text-xs text-slate-500">Send booking and reminder notifications for class participants.</span>
                  </span>
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="flex items-start gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                    checked={Boolean(form.classOnlineBookingEnabled ?? true)}
                    onChange={(event) => setForm((prev) => ({ ...prev, classOnlineBookingEnabled: event.target.checked, portalSelfScheduleEnabled: event.target.checked ? true : prev.portalSelfScheduleEnabled }))}
                  />
                  <span>
                    <span className="block font-medium text-slate-700">Online class booking</span>
                    <span className="block text-xs text-slate-500">Allow this class to appear in public booking links.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                    checked={Boolean(form.classPortalVisible ?? true)}
                    onChange={(event) => setForm((prev) => ({ ...prev, classPortalVisible: event.target.checked }))}
                  />
                  <span>
                    <span className="block font-medium text-slate-700">Client portal classes</span>
                    <span className="block text-xs text-slate-500">Show booked classes as class entries in portal appointment views.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                    checked={Boolean(form.classWaitlistEnabled)}
                    onChange={(event) => setForm((prev) => ({ ...prev, classWaitlistEnabled: event.target.checked }))}
                  />
                  <span>
                    <span className="block font-medium text-slate-700">Class waitlist</span>
                    <span className="block text-xs text-slate-500">Track demand when a class reaches capacity.</span>
                  </span>
                </label>
              </div>
              <label className="space-y-1 text-sm">
                <span className="font-medium text-slate-500">Participant instructions</span>
                <textarea
                  className="min-h-[86px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                  value={form.classParticipantInstructions || ''}
                  onChange={(event) => setForm((prev) => ({ ...prev, classParticipantInstructions: event.target.value }))}
                  placeholder="What attendees should bring, arrival rules, cancellation notes"
                />
              </label>
            </div>

            <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 md:col-span-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="text-sm font-medium text-slate-600">Virtual services</span>
                  <p className="text-xs text-slate-500">Configure online delivery, video session creation, and patient support details.</p>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${form.isTelemed ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-500'}`}>
                  {form.isTelemed ? 'Video enabled' : 'In-person'}
                </span>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-500">Delivery mode</span>
                  <select
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    value={form.virtualDeliveryMode || 'in_person'}
                    onChange={(event) => {
                      const deliveryMode = event.target.value as 'in_person' | 'virtual' | 'hybrid';
                      setForm((prev) => ({ ...prev, virtualDeliveryMode: deliveryMode, isTelemed: deliveryMode !== 'in_person' }));
                    }}
                  >
                    <option value="in_person">In-person</option>
                    <option value="virtual">Virtual</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-500">Video provider override</span>
                  <input
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    value={form.videoProviderOverride || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, videoProviderOverride: event.target.value }))}
                    placeholder="Use global provider"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-500">Max participants</span>
                  <input
                    type="number"
                    min={2}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    value={form.maxVideoParticipants ?? 2}
                    onChange={(event) => setForm((prev) => ({ ...prev, maxVideoParticipants: Math.max(2, Number(event.target.value) || 2) }))}
                  />
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex items-start gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                    checked={Boolean(form.autoCreateVideoSession ?? true)}
                    onChange={(event) => setForm((prev) => ({ ...prev, autoCreateVideoSession: event.target.checked }))}
                  />
                  <span>
                    <span className="block font-medium text-slate-700">Create video link when booked</span>
                    <span className="block text-xs text-slate-500">Appointments for this service generate a telemedicine session automatically.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                    checked={Boolean(form.groupVideoEnabled)}
                    onChange={(event) => setForm((prev) => ({ ...prev, groupVideoEnabled: event.target.checked, isTelemed: event.target.checked ? true : prev.isTelemed }))}
                  />
                  <span>
                    <span className="block font-medium text-slate-700">Group video appointments</span>
                    <span className="block text-xs text-slate-500">Allow multiple staff or participants to join the consultation room.</span>
                  </span>
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-500">Video consultation support</span>
                  <textarea
                    className="min-h-[92px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    value={form.videoConsultationInstructions || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, videoConsultationInstructions: event.target.value }))}
                    placeholder="Pre-visit checklist, camera guidance, consent reminders"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-slate-500">Video FAQ / troubleshooting</span>
                  <textarea
                    className="min-h-[92px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    value={form.videoTroubleshootingFaq || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, videoTroubleshootingFaq: event.target.value }))}
                    placeholder="What to do if the link, audio, or camera does not work"
                  />
                </label>
              </div>
            </div>

            <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 md:col-span-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-medium text-slate-600">Service bundle</span>
                  <p className="text-xs text-slate-500">Select services that are sold or booked together with this service.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">{(form.bundledServiceIds || []).length} selected</span>
              </div>
              <div className="grid max-h-44 gap-2 overflow-y-auto sm:grid-cols-2">
                {serviceOptions.map((service) => {
                  const checked = (form.bundledServiceIds || []).includes(service.id);
                  return (
                    <label key={service.id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => setForm((prev) => {
                          const current = prev.bundledServiceIds || [];
                          return {
                            ...prev,
                            bundledServiceIds: event.target.checked
                              ? Array.from(new Set([...current, service.id]))
                              : current.filter((item) => item !== service.id),
                          };
                        })}
                        className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                      />
                      <span className="min-w-0 truncate">{service.name}</span>
                    </label>
                  );
                })}
                {!serviceOptions.length ? <p className="text-sm text-slate-500">No other services available.</p> : null}
              </div>
            </div>
            <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 md:col-span-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-medium text-slate-600">Employee service durations</span>
                  <p className="text-xs text-slate-500">Override this service duration for specific employees.</p>
                </div>
                <button type="button" onClick={addEmployeeDuration} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  Add duration
                </button>
              </div>
              <div className="space-y-2">
                {Object.entries(form.employeeDurations || {}).map(([employeeId, minutes]) => (
                  <div key={employeeId} className="grid gap-2 sm:grid-cols-[1fr_120px_36px]">
                    <input
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      value={employeeId}
                      onChange={(event) => {
                        const nextId = event.target.value;
                        setForm((prev) => {
                          const next = { ...(prev.employeeDurations || {}) };
                          delete next[employeeId];
                          if (nextId.trim()) next[nextId.trim()] = Number(minutes || 0);
                          return { ...prev, employeeDurations: next };
                        });
                      }}
                      placeholder="Employee ID"
                    />
                    <input
                      type="number"
                      min={0}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      value={minutes}
                      onChange={(event) => updateEmployeeDuration(employeeId, Number(event.target.value) || 0)}
                    />
                    <button type="button" onClick={() => removeEmployeeDuration(employeeId)} className="rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50" aria-label={`Remove ${employeeId}`}>
                      ×
                    </button>
                  </div>
                ))}
                {!Object.keys(form.employeeDurations || {}).length ? <p className="text-sm text-slate-500">No employee-specific durations.</p> : null}
              </div>
            </div>
            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3 md:col-span-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-medium text-slate-600">Service charges</span>
                  <p className="text-xs text-slate-500">Create charges linked to this service. Checkout can apply, edit, or remove them as POS line items.</p>
                </div>
                <button type="button" onClick={addServiceCharge} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  Add charge
                </button>
              </div>
              <div className="space-y-2">
                {(form.serviceCharges || []).map((charge) => (
                  <div key={charge.id} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-2 md:grid-cols-[1fr_140px_110px_120px_80px_36px]">
                    <input
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={charge.name}
                      onChange={(event) => updateServiceCharge(charge.id, { name: event.target.value })}
                      placeholder="Charge name"
                    />
                    <input
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm uppercase"
                      value={charge.code}
                      onChange={(event) => updateServiceCharge(charge.id, { code: event.target.value.toUpperCase() })}
                      placeholder="Code"
                    />
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={charge.amount}
                      onChange={(event) => updateServiceCharge(charge.id, { amount: Number(event.target.value) || 0 })}
                    />
                    <select
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={charge.type}
                      onChange={(event) => updateServiceCharge(charge.id, { type: event.target.value as 'flat' | 'percent' })}
                    >
                      <option value="flat">Flat</option>
                      <option value="percent">Percent</option>
                    </select>
                    <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={charge.active}
                        onChange={(event) => updateServiceCharge(charge.id, { active: event.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                      />
                      Active
                    </label>
                    <button type="button" onClick={() => deleteServiceCharge(charge.id)} className="rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50" aria-label={`Delete ${charge.name || 'service charge'}`}>
                      ×
                    </button>
                  </div>
                ))}
                {!(form.serviceCharges || []).length ? <p className="text-sm text-slate-500">No service charges configured.</p> : null}
              </div>
            </div>
            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3 md:col-span-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-medium text-slate-600">Required equipment</span>
                  <p className="text-xs text-slate-500">Assign equipment used by this service. Matching assets are preselected during appointment booking.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">{(form.equipmentResourceIds || []).length} selected</span>
              </div>
              <div className="grid max-h-52 gap-2 overflow-y-auto sm:grid-cols-2">
                {resourceOptions.filter((resource) => !isRoomResourceType(resource.type)).map((resource) => {
                  const selected = (form.equipmentResourceIds || []).includes(resource.id);
                  return (
                    <label key={resource.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-slate-700">{resource.name}</span>
                        <span className="block truncate text-xs text-slate-500">{resource.type}{resource.locationId ? ` · ${resource.locationId}` : ''}</span>
                      </span>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(event) => setForm((prev) => {
                          const current = prev.equipmentResourceIds || [];
                          return {
                            ...prev,
                            equipmentResourceIds: event.target.checked
                              ? Array.from(new Set([...current, resource.id]))
                              : current.filter((item) => item !== resource.id),
                          };
                        })}
                        className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
                      />
                    </label>
                  );
                })}
                {!resourceOptions.filter((resource) => !isRoomResourceType(resource.type)).length ? <p className="text-sm text-slate-500">No equipment resources configured.</p> : null}
              </div>
            </div>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Complexity</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.complexity || ''}
                onChange={(event) => setForm((prev) => ({ ...prev, complexity: event.target.value }))}
                placeholder="Short complexity note"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Legacy billing summary</span>
              <input
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500 outline-none"
                value={form.billingCodes || ''}
                readOnly
                placeholder="Auto-generated from billing code rows"
              />
            </label>
          </div>

          <label className="block space-y-1 text-sm">
            <span className="flex items-center justify-between gap-3">
              <span className="font-medium text-slate-500">Description</span>
              <button type="button" onClick={() => void generateServiceDescription()} disabled={descriptionGenerating} className="inline-flex items-center gap-2 rounded-lg border border-sky-200 px-3 py-1.5 text-xs font-semibold text-sky-800 hover:bg-sky-50 disabled:opacity-50">
                {descriptionGenerating ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} />} Service Description Generator
              </button>
            </span>
            <textarea
              className="h-24 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
              value={form.description || ''}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Patient-facing or staff-facing service description"
            />
          </label>

          <label className="block space-y-1 text-sm">
            <span className="font-medium text-slate-500">Instructions</span>
            <textarea
              className="h-28 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
              value={form.instructions || ''}
              onChange={(event) => setForm((prev) => ({ ...prev, instructions: event.target.value }))}
              placeholder="Operational notes for staff"
            />
          </label>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-slate-500">Location-specific overrides JSON</span>
              <textarea
                className="h-40 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={locationOverridesJson}
                onChange={(event) => setLocationOverridesJson(event.target.value)}
                placeholder='[{"locationId":"loc_123","price":150,"durationMinutes":45,"available":true}]'
              />
            </label>
            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 lg:col-span-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-ink">Products used by this service</h4>
                  <p className="text-xs text-slate-500">Build the default stock recipe from clinic inventory items and billable products.</p>
                </div>
                <button type="button" onClick={addStockRecipeRow} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <Plus size={14} /> Add product
                </button>
              </div>
              {stockRecipeRows.length ? (
                <div className="space-y-3">
                  {stockRecipeRows.map((row, index) => (
                    <div key={index} className="grid gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3 md:grid-cols-[1.2fr_1.2fr_1fr_90px_90px_36px]">
                      <select
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={row.itemId || ''}
                        onChange={(event) => selectInventoryItem(index, event.target.value)}
                      >
                        <option value="">Inventory item</option>
                        {inventoryItems.map((item) => (
                          <option key={item.id} value={item.id}>{item.name}{item.strength ? ` - ${item.strength}` : ''}</option>
                        ))}
                      </select>
                      <select
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={row.billingProductId || ''}
                        onChange={(event) => selectBillingProduct(index, event.target.value)}
                      >
                        <option value="">Billable product</option>
                        {billingProducts.map((product) => (
                          <option key={product.id} value={product.id}>{product.code} - {product.name}</option>
                        ))}
                      </select>
                      <input
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={row.itemName}
                        onChange={(event) => updateStockRecipeRow(index, { itemName: event.target.value })}
                        placeholder="Display name"
                      />
                      <input
                        type="number"
                        min={0.01}
                        step={0.01}
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={row.quantity}
                        onChange={(event) => updateStockRecipeRow(index, { quantity: Number(event.target.value) || 1 })}
                      />
                      <input
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                        value={row.unit}
                        onChange={(event) => updateStockRecipeRow(index, { unit: event.target.value })}
                        placeholder="Unit"
                      />
                      <button
                        type="button"
                        onClick={() => removeStockRecipeRow(index)}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50"
                        aria-label="Remove product"
                        title="Remove product"
                      >
                        <Trash2 size={14} />
                      </button>
                      <input
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm md:col-span-3"
                        value={row.brand || ''}
                        onChange={(event) => updateStockRecipeRow(index, { brand: event.target.value })}
                        placeholder="Brand or substitution note"
                      />
                      <input
                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm md:col-span-3"
                        value={row.notes || ''}
                        onChange={(event) => updateStockRecipeRow(index, { notes: event.target.value })}
                        placeholder="Preparation or usage notes"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  No stock recipe products yet. Add the consumables normally used when this service is completed.
                </div>
              )}
              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer font-medium text-slate-600">Advanced JSON view</summary>
                <textarea
                  className="mt-2 h-32 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                  value={defaultStockRecipeJson}
                  onChange={(event) => {
                    setDefaultStockRecipeJson(event.target.value);
                    try {
                      setStockRecipeRows(normalizeStockRecipe(JSON.parse(event.target.value || '[]')));
                    } catch {
                      // Keep raw JSON text while the user is typing invalid JSON.
                    }
                  }}
                  placeholder='[{"itemName":"Vitamin B12","quantity":1,"unit":"ampule"}]'
                />
              </details>
            </div>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-slate-500">Care instructions JSON</span>
              <textarea
                className="h-40 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={careInstructionsJson}
                onChange={(event) => setCareInstructionsJson(event.target.value)}
                placeholder='{"preCare":"Hydrate before treatment","postCare":"Rest after treatment","aftercare":"Call if symptoms worsen","followUp":"Check in after 48 hours","locationOverrides":[]}'
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-slate-500">Service documentation JSON</span>
              <textarea
                className="h-40 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={serviceDocumentationJson}
                onChange={(event) => setServiceDocumentationJson(event.target.value)}
                placeholder='{"intakeFormTemplateIds":[],"consentFormTemplateIds":[],"contraindicationQuestions":["Any allergies?"],"signedAcknowledgementRequired":true,"requireCompletionBeforeTreatment":true}'
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="font-medium text-slate-500">Clinical safety rules JSON</span>
              <textarea
                className="h-40 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={clinicalSafetyRulesJson}
                onChange={(event) => setClinicalSafetyRulesJson(event.target.value)}
                placeholder='{"enabled":true,"requireAllergyReview":true,"requireMedicationReview":true,"doctorEscalationRequired":false,"adverseEventRecordingEnabled":true}'
              />
            </label>
          </div>
        </div>

        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-ink">Billing Codes</h3>
              <p className="text-sm text-slate-500">One appointment type can map to many billing rows.</p>
            </div>
            <button
              type="button"
              onClick={() => openBillingCodeModal()}
              disabled={mode === 'new'}
              className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm text-white shadow-soft disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Plus size={16} /> Add new billing code
            </button>
          </div>

          {mode === 'new' ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
              Save the appointment type first, then add billing codes.
            </div>
          ) : null}

          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="p-3">Code</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Units</th>
                  <th className="p-3">Modifier</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Notes</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {billingCodes.map((row) => (
                  <tr
                    key={row.id || row.code}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => openBillingCodeModal(row)}
                  >
                    <td className="p-3 font-medium text-ink">{row.code}</td>
                    <td className="p-3 text-slate-600">{row.description || '—'}</td>
                    <td className="p-3 text-slate-600">{row.units}</td>
                    <td className="p-3 text-slate-600">{row.modifier || '—'}</td>
                    <td className="p-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {row.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">{row.notes || '—'}</td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openBillingCodeModal(row);
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
                          title="Edit"
                          aria-label="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            void deleteBillingCode(row.id);
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50"
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!billingCodes.length ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-slate-500">
                      No billing codes have been added yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {billingCodeError ? <div className="text-sm text-rose-600">{billingCodeError}</div> : null}
        </div>

        <div className="card p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-ink">Cancellation Rules</h3>
              <p className="text-sm text-slate-500">Structured rules that can later drive portal logic and reminders.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Minimum notice hours</span>
              <input
                type="number"
                min={0}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.cancellationNoticeHours}
                onChange={(event) => setForm((prev) => ({ ...prev, cancellationNoticeHours: Number(event.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Grace period minutes</span>
              <input
                type="number"
                min={0}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.cancellationGracePeriodMinutes}
                onChange={(event) => setForm((prev) => ({ ...prev, cancellationGracePeriodMinutes: Number(event.target.value) || 0 }))}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Late cancellation fee amount</span>
              <input
                type="number"
                min={0}
                step="0.01"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.lateCancellationFeeAmount ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm((prev) => ({ ...prev, lateCancellationFeeAmount: value === '' ? null : Number(value) }));
                }}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium text-slate-500">Fee type</span>
              <select
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
                value={form.lateCancellationFeeType}
                onChange={(event) => setForm((prev) => ({ ...prev, lateCancellationFeeType: event.target.value as 'flat' | 'percent' }))}
              >
                <option value="flat">Flat fee</option>
                <option value="percent">Percent of visit</option>
              </select>
            </label>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={form.allowPortalCancellation}
                onChange={(event) => setForm((prev) => ({ ...prev, allowPortalCancellation: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Allow portal cancellation</span>
            </div>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={form.chargeNoShowFee}
                onChange={(event) => setForm((prev) => ({ ...prev, chargeNoShowFee: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Charge no-show fee</span>
            </div>
            <div className="flex items-center gap-3 pt-6 text-sm">
              <input
                type="checkbox"
                checked={form.requireCancellationReason}
                onChange={(event) => setForm((prev) => ({ ...prev, requireCancellationReason: event.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-ink focus:ring-ink"
              />
              <span className="text-slate-700">Require cancellation reason</span>
            </div>
          </div>
        </div>
      </form>

      <BillingCodeModal
        open={billingCodeModalOpen}
        form={billingCodeForm}
        onClose={() => setBillingCodeModalOpen(false)}
        onChange={setBillingCodeForm}
        onSave={saveBillingCode}
        saving={billingCodeSaving}
        isEdit={Boolean(billingCodeForm.id)}
        catalogRows={catalogRows}
        catalogLoading={catalogLoading}
      />
    </div>
  );
}

export default function SettingsAppointmentTypes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const { appointmentTypeId } = useParams<{ appointmentTypeId?: string }>();
  const isNew = location.pathname === '/settings/appointmenttypes/new';
  const isDetail = isNew || Boolean(appointmentTypeId);
  const [rows, setRows] = useState<AppointmentTypePayload[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [serviceVariantsEnabled, setServiceVariantsEnabled] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  const getToken = useCallback(async () => {
    return getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
  }, [getAccessTokenSilently]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/appointment-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Load failed ${res.status}`);
      const data = (await res.json()) as AppointmentTypePayload[];
      setRows(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load appointment types');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const loadConfig = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/appointment-types/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Load failed ${res.status}`);
      const data = (await res.json()) as TreatmentCatalogConfig;
      setServiceVariantsEnabled(Boolean(data.enableServiceVariants));
    } catch {
      setServiceVariantsEnabled(false);
    }
  }, [getToken]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!isDetail) {
      void loadList();
    }
  }, [isDetail, loadList]);

  const handleToggleServiceVariants = useCallback(async (next: boolean) => {
    setConfigSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/appointment-types/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enableServiceVariants: next }),
      });
      if (!res.ok) throw new Error(`Save failed ${res.status}`);
      setServiceVariantsEnabled(next);
      setSuccess(next ? 'Service options enabled' : 'Service options disabled');
      setTimeout(() => setSuccess(null), 2500);
      if (!isDetail) {
        await loadList();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update service option setting');
    } finally {
      setConfigSaving(false);
    }
  }, [getToken, isDetail, loadList]);

  const handleDelete = useCallback(
    async (id?: string) => {
      if (!id) return;
      if (!window.confirm('Delete this appointment type?')) return;
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/appointment-types/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Delete failed ${res.status}`);
        await loadList();
        setSuccess('Deleted');
        setTimeout(() => setSuccess(null), 2500);
      } catch (err: any) {
        setError(err.message || 'Failed to delete appointment type');
      }
    },
    [getToken, loadList],
  );

  const handleUpdate = useCallback(
    async (id: string, patch: Partial<AppointmentTypeRow>) => {
      const row = rows.find((item) => item.id === id);
      if (!row) return;
      setError(null);
      try {
        const token = await getToken();
        const currentDocumentation = row.serviceDocumentation && typeof row.serviceDocumentation === 'object'
          ? row.serviceDocumentation
          : {};
        const currentMeta = serviceMeta(row);
        const nextDocumentation = {
          ...currentDocumentation,
          serviceMeta: {
            ...currentMeta,
            serviceGroup: patch.serviceGroup !== undefined ? patch.serviceGroup || '' : currentMeta.serviceGroup,
            taxName: patch.taxName !== undefined ? patch.taxName || '' : currentMeta.taxName,
            taxRate: patch.taxRate !== undefined ? Number(patch.taxRate || 0) : currentMeta.taxRate,
            displayOrder: patch.displayOrder !== undefined
              ? (patch.displayOrder === null ? null : Number(patch.displayOrder))
              : currentMeta.displayOrder,
            categoryColor: patch.categoryColor !== undefined ? patch.categoryColor || '#0ea5e9' : currentMeta.categoryColor,
            serviceCode: patch.serviceCode !== undefined ? patch.serviceCode || '' : currentMeta.serviceCode,
            iscCode: patch.iscCode !== undefined ? patch.iscCode || '' : currentMeta.iscCode,
            ccsdCode: patch.ccsdCode !== undefined ? patch.ccsdCode || '' : currentMeta.ccsdCode,
            bufferBeforeMinutes: patch.bufferBeforeMinutes !== undefined ? Number(patch.bufferBeforeMinutes || 0) : currentMeta.bufferBeforeMinutes,
            bufferAfterMinutes: patch.bufferAfterMinutes !== undefined ? Number(patch.bufferAfterMinutes || 0) : currentMeta.bufferAfterMinutes,
            extraBeforeMinutes: patch.extraBeforeMinutes !== undefined ? Number(patch.extraBeforeMinutes || 0) : currentMeta.extraBeforeMinutes,
            extraAfterMinutes: patch.extraAfterMinutes !== undefined ? Number(patch.extraAfterMinutes || 0) : currentMeta.extraAfterMinutes,
            employeeDurations: patch.employeeDurations !== undefined ? patch.employeeDurations || {} : currentMeta.employeeDurations,
            bookingLinkSlug: patch.bookingLinkSlug !== undefined ? patch.bookingLinkSlug || '' : currentMeta.bookingLinkSlug,
            bundledServiceIds: patch.bundledServiceIds !== undefined ? patch.bundledServiceIds || [] : currentMeta.bundledServiceIds,
            pointOfSaleEnabled: patch.pointOfSaleEnabled !== undefined ? Boolean(patch.pointOfSaleEnabled) : currentMeta.pointOfSaleEnabled,
            serviceCharges: patch.serviceCharges !== undefined ? (patch.serviceCharges || []).map(normalizeServiceCharge) : currentMeta.serviceCharges,
            equipmentResourceIds: patch.equipmentResourceIds !== undefined ? patch.equipmentResourceIds || [] : currentMeta.equipmentResourceIds,
            virtualDeliveryMode: patch.virtualDeliveryMode !== undefined ? patch.virtualDeliveryMode || 'in_person' : currentMeta.virtualDeliveryMode,
            videoProviderOverride: patch.videoProviderOverride !== undefined ? patch.videoProviderOverride || '' : currentMeta.videoProviderOverride,
            groupVideoEnabled: patch.groupVideoEnabled !== undefined ? Boolean(patch.groupVideoEnabled) : currentMeta.groupVideoEnabled,
            maxVideoParticipants: patch.maxVideoParticipants !== undefined ? Number(patch.maxVideoParticipants || 2) : currentMeta.maxVideoParticipants,
            autoCreateVideoSession: patch.autoCreateVideoSession !== undefined ? Boolean(patch.autoCreateVideoSession) : currentMeta.autoCreateVideoSession,
            videoConsultationInstructions: patch.videoConsultationInstructions !== undefined ? patch.videoConsultationInstructions || '' : currentMeta.videoConsultationInstructions,
            videoTroubleshootingFaq: patch.videoTroubleshootingFaq !== undefined ? patch.videoTroubleshootingFaq || '' : currentMeta.videoTroubleshootingFaq,
            isClass: patch.isClass !== undefined ? Boolean(patch.isClass) : currentMeta.isClass,
            classCapacity: patch.classCapacity !== undefined ? Number(patch.classCapacity || 1) : currentMeta.classCapacity,
            classOnlineBookingEnabled: patch.classOnlineBookingEnabled !== undefined ? Boolean(patch.classOnlineBookingEnabled) : currentMeta.classOnlineBookingEnabled,
            classPortalVisible: patch.classPortalVisible !== undefined ? Boolean(patch.classPortalVisible) : currentMeta.classPortalVisible,
            classNotificationsEnabled: patch.classNotificationsEnabled !== undefined ? Boolean(patch.classNotificationsEnabled) : currentMeta.classNotificationsEnabled,
            classWaitlistEnabled: patch.classWaitlistEnabled !== undefined ? Boolean(patch.classWaitlistEnabled) : currentMeta.classWaitlistEnabled,
            classParticipantInstructions: patch.classParticipantInstructions !== undefined ? patch.classParticipantInstructions || '' : currentMeta.classParticipantInstructions,
          },
        };
        const payload = {
          name: patch.name ?? row.name,
          category: patch.category !== undefined ? patch.category || null : row.category || null,
          description: patch.description !== undefined ? patch.description || null : row.description || null,
          durationMinutes: patch.durationMinutes ?? row.durationMinutes,
          price: patch.price ?? row.price,
          currency: patch.currency ?? row.currency,
          available: patch.available ?? row.available,
          bookingVisibility: patch.bookingVisibility ?? row.bookingVisibility,
          locationOverrides: patch.locationOverrides ?? row.locationOverrides ?? [],
          defaultStockRecipe: patch.defaultStockRecipe ?? row.defaultStockRecipe ?? [],
          careInstructions: patch.careInstructions ?? row.careInstructions ?? {},
          serviceDocumentation: nextDocumentation,
          clinicalSafetyRules: patch.clinicalSafetyRules ?? row.clinicalSafetyRules ?? {},
          color: patch.color ?? row.color,
          isTelemed: patch.isTelemed ?? row.isTelemed,
          portalSelfScheduleEnabled: patch.portalSelfScheduleEnabled ?? row.portalSelfScheduleEnabled,
          existingPatientsOnly: patch.existingPatientsOnly ?? row.existingPatientsOnly,
          allowNewPatients: patch.allowNewPatients ?? row.allowNewPatients,
          portalDisplayName: patch.portalDisplayName !== undefined ? patch.portalDisplayName || null : row.portalDisplayName || null,
          billingCodes: patch.billingCodes !== undefined ? patch.billingCodes || null : row.billingCodes || null,
          cancellationRule: patch.cancellationRule !== undefined ? patch.cancellationRule || null : row.cancellationRule || null,
          allowPortalCancellation: patch.allowPortalCancellation ?? row.allowPortalCancellation,
          cancellationNoticeHours: patch.cancellationNoticeHours ?? row.cancellationNoticeHours,
          cancellationGracePeriodMinutes: patch.cancellationGracePeriodMinutes ?? row.cancellationGracePeriodMinutes,
          lateCancellationFeeAmount: patch.lateCancellationFeeAmount !== undefined ? patch.lateCancellationFeeAmount : row.lateCancellationFeeAmount,
          lateCancellationFeeType: patch.lateCancellationFeeType ?? row.lateCancellationFeeType,
          chargeNoShowFee: patch.chargeNoShowFee ?? row.chargeNoShowFee,
          requireCancellationReason: patch.requireCancellationReason ?? row.requireCancellationReason,
          priority: patch.priority ?? row.priority,
          complexity: patch.complexity !== undefined ? patch.complexity || null : row.complexity || null,
          instructions: patch.instructions !== undefined ? patch.instructions || null : row.instructions || null,
        };
        const res = await fetch(`${API_BASE}/appointment-types/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(`Update failed ${res.status}`);
        await loadList();
        setSuccess('Saved');
        setTimeout(() => setSuccess(null), 2500);
      } catch (err: any) {
        setError(err.message || 'Failed to update service');
      }
    },
    [getToken, loadList, rows],
  );

  if (isDetail) {
    return (
      <AppointmentTypeDetailPage
        mode={isNew ? 'new' : 'edit'}
        id={isNew ? undefined : appointmentTypeId}
        serviceVariantsEnabled={serviceVariantsEnabled}
        onBack={() => navigate('/settings/appointmenttypes')}
        onSaved={loadList}
      />
    );
  }

  return (
    <AppointmentTypeListPage
      rows={rows}
      loading={loading}
      error={error}
      success={success}
      serviceVariantsEnabled={serviceVariantsEnabled}
      configSaving={configSaving}
      onAdd={() => navigate('/settings/appointmenttypes/new')}
      onOpen={(id) => navigate(`/settings/appointmenttypes/${id}`)}
      onDelete={(id) => void handleDelete(id)}
      onUpdate={(id, patch) => void handleUpdate(id, patch)}
      onToggleServiceVariants={(next) => void handleToggleServiceVariants(next)}
      onBack={() => navigate('/settings')}
    />
  );
}
